// src/tattoo/PreviewPane.jsx
import React, {
  Suspense,
  useRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import * as THREE from "three";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import {
  OrbitControls,
  Environment,
  MeshReflectorMaterial,
} from "@react-three/drei";
import { useSnapshot } from "valtio";
import {
  tattoo,
  setSkinColor,
  setPoseMorphNames,
  saveProjectToFile,
  setLayerProp,
  markDirty,
} from "./state";
import PartModel from "./PartModel.jsx";
import SaveShareButton from "./SaveShareButton.jsx";
import useMediaQuery from "./useMediaQuery";
import { GuidePaint, guidePaintRef } from "./GuidePaint";

import LogoOverlay from "./LogoOverlay.jsx";
import ProjectHeart from "./ProjectHeart.jsx";
import OverlayPaintControlsPortal from "./OverlayPaintControlsPortal.jsx";
import MobileBottomDock from "./MobileBottomDock.jsx";
import { useAuth } from "../auth/AuthProvider.jsx";
import IdleMotion from "./IdleMotion.jsx";
import { ContactShadows } from "@react-three/drei";
import { fastSetLayerTxTy, addDecalFromFile } from "./state.js";
import {
  Save,
  LayoutDashboard,
  Monitor,
  Camera,
  Pencil,
  Dumbbell,
  Move,
  FlipHorizontal,
  Check,
  SlidersHorizontal,
  ArrowLeft,
  RotateCw,
  RotateCcw,
} from "lucide-react";

function showToast(msg) {
  try {
    window.showToast?.(msg);
  } catch {}
}

const isIOS =
  (typeof navigator !== "undefined" &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (/Macintosh/.test(navigator.userAgent) &&
        typeof window !== "undefined" &&
        "ontouchend" in window))) ||
  false;

const HOME_KEY = (modelKey) => `tt_home_cam__${modelKey}`;

const PROXY_LAYER = 2; // any layer != 0

const DockIcon = ({ icon: Icon, size = 18, ...props }) => (
  <Icon size={size} strokeWidth={2} {...props} />
);

/* ---------------- tiny safe helpers ---------------- */

function gpSetFullUV() {
  const gp = guidePaintRef.current;
  if (!gp) return;

  try {
    gp.setUvRect?.({ x: 0, y: 0, w: 1, h: 1 });
  } catch {}
  try {
    gp.setCalibration?.({ uScale: 1, vScale: 1, uBias: 0, vBias: 0 });
  } catch {}

  // if you ever flip V elsewhere, keep it consistent:
  // gp.FLIP_V = false; // only if your decals expect non-flipped UVs
}

function gpRestorePaintUV(prev) {
  const gp = guidePaintRef.current;
  if (!gp || !prev) return;

  try {
    gp.setUvRect?.(prev.uvRect);
  } catch {}
  try {
    gp.setCalibration?.(prev.calib);
  } catch {}
  // if you captured FLIP_V, restore it too
}

function safeLoadHome(modelKey) {
  try {
    const raw = localStorage.getItem(HOME_KEY(modelKey));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function safeSaveHome(modelKey, snap) {
  try {
    localStorage.setItem(HOME_KEY(modelKey), JSON.stringify(snap));
  } catch {}
}

function saveHomeSnap(modelKey, snap) {
  safeSaveHome(modelKey, snap);
  try {
    window.__tt_home_cam = snap;
  } catch {}
}

function WebGLCleanup() {
  const { gl, scene } = useThree();

  useEffect(() => {
    return () => {
      try {
        // Dispose geometries/materials/textures
        scene.traverse((obj) => {
          if (obj.geometry) obj.geometry.dispose?.();

          const mat = obj.material;
          if (mat) {
            const materials = Array.isArray(mat) ? mat : [mat];
            for (const m of materials) {
              // dispose any textures on the material
              for (const k in m) {
                const v = m[k];
                if (v && v.isTexture) v.dispose?.();
              }
              m.dispose?.();
            }
          }
        });

        gl.renderLists?.dispose?.();
        gl.dispose?.();

        // Tell browser to actually drop the context (Safari/mobile loves this)
        // Tell browser to drop the context IF supported
        const ctx = gl.getContext?.();
        const ext = ctx?.getExtension?.("WEBGL_lose_context");
        if (ext && typeof ext.loseContext === "function") {
          ext.loseContext();
        }
      } catch (e) {
        console.warn("[webgl] cleanup failed", e);
      }
    };
  }, [gl, scene]);

  return null;
}

function StudioLoadingOverlay({
  title = "Entering the Studio…",
  subtitle = "Loading garments & textures",
}) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 9999,
        display: "grid",
        placeItems: "center",
        background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        pointerEvents: "auto",
      }}
    >
      <div
        style={{
          width: "min(420px, 92vw)",
          borderRadius: 18,
          padding: 18,
          border: "1px solid rgba(255,255,255,0.14)",
          background: "rgba(20,20,20,0.55)",
          boxShadow: "0 18px 70px rgba(0,0,0,0.55)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(255,255,255,0.08)",
              display: "grid",
              placeItems: "center",
              fontSize: 18,
            }}
          >
            ✦
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 650, color: "#fff" }}>
              {title}
            </div>
            <div
              style={{
                fontSize: 12,
                opacity: 0.8,
                marginTop: 2,
                color: "#fff",
              }}
            >
              {subtitle}
            </div>
          </div>

          <div
            aria-label="loading"
            style={{
              width: 18,
              height: 18,
              borderRadius: 999,
              border: "2px solid rgba(255,255,255,0.25)",
              borderTopColor: "rgba(255,255,255,0.9)",
              animation: "ttSpin 0.9s linear infinite",
            }}
          />
        </div>

        <div
          style={{ marginTop: 12, fontSize: 12, opacity: 0.75, color: "#fff" }}
        >
          Tip: paint a little, lift, orbit, paint again 👌
        </div>
      </div>

      <style>{`
        @keyframes ttSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

function boundsOfMeshes(meshes) {
  const box = new THREE.Box3();
  const tmp = new THREE.Box3();
  meshes.forEach((m) => box.union(tmp.setFromObject(m)));
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  return { box, size, center };
}

function snapRootToFloor(root, floorY = 0) {
  if (!root) return;
  root.updateWorldMatrix?.(true, true);

  // bounds of visible geometry only
  const box = new THREE.Box3().setFromObject(root);
  const minY = box.min.y;

  if (!isFinite(minY)) return;

  const dy = floorY - minY;
  root.position.y += dy;

  root.updateWorldMatrix?.(true, true);
}

/* ---------------- helpers ---------------- */

function isProbablyBodyMesh(o) {
  if (!o?.isMesh && !o?.isSkinnedMesh) return false;

  const n = (o.name || "").toLowerCase();

  // excludes
  if (
    /(shorts|pants|bottom|top|shirt|tank|bra|hat|hair|shoe|cloth|garment)/i.test(
      n
    )
  ) {
    return false;
  }

  // includes
  if (/(female\.?hr|femalehr|malehr|body|skin|torso)/i.test(n)) {
    return true;
  }

  // fallback
  return !!o.isSkinnedMesh;
}

function centerOrbitOn(rootOrMesh, controls, { yBias = 0 } = {}) {
  if (!rootOrMesh || !controls) return;
  const box = new THREE.Box3().setFromObject(rootOrMesh);
  const c = box.getCenter(new THREE.Vector3());
  controls.target.set(c.x, c.y + yBias, c.z);
  controls.update?.();
}

function applyMorphsToRoot(root, morphs = {}) {
  if (!root || typeof root.traverse !== "function") return;
  root.traverse((n) => {
    if (!n?.isMesh || !n.morphTargetDictionary || !n.morphTargetInfluences)
      return;
    const dict = n.morphTargetDictionary;
    for (const [name, val] of Object.entries(morphs)) {
      const idx = dict[name];
      if (idx !== undefined) n.morphTargetInfluences[idx] = val;
    }
    const mats = Array.isArray(n.material) ? n.material : [n.material];
    mats.forEach((m) => m && (m.needsUpdate = true));
  });
  try {
    window.dispatchEvent(new CustomEvent("morph:changed"));
  } catch {}
}

function fitToObjects(
  camera,
  controls,
  objects,
  { pad = 1.12, biasY = -0.12 }
) {
  const box = new THREE.Box3();
  const tmp = new THREE.Box3();
  for (const o of objects) box.union(tmp.setFromObject(o));
  const sz = new THREE.Vector3();
  box.getSize(sz);
  if (!isFinite(sz.x) || !isFinite(sz.y) || !isFinite(sz.z)) return null;

  const center = new THREE.Vector3();
  box.getCenter(center);
  const maxDim = Math.max(sz.x, sz.y, sz.z);
  center.y += biasY * maxDim;

  const fov = camera.fov * (Math.PI / 180);
  let dist = maxDim / 2 / Math.tan(fov / 2);
  dist *= pad;

  const pos = center
    .clone()
    .addScaledVector(new THREE.Vector3(0, 0.15, 1).normalize(), dist);

  camera.position.copy(pos);
  camera.near = Math.max(0.01, dist / 100);
  camera.far = dist * 10 + maxDim;
  camera.updateProjectionMatrix();

  if (controls) {
    controls.target.copy(center);
    controls.minDistance = Math.max(0.02, dist * 0.08);
    controls.maxDistance = Math.max(4, dist * 6.0);
    controls.update();
  }
  return { pos, target: center, dist };
}

function applyCameraSnapshot(camera, controls, snap) {
  if (!snap || !camera) return;
  try {
    if (!window.__tt_cam_log_once) {
      window.__tt_cam_log_once = true;
      console.log("[CAM] applying camera snapshot", snap);
    }
    const [px, py, pz] = snap.pos || [0, 1.0, 2.2];
    const [tx, ty, tz] = snap.target || [0, 0, 0];
    camera.position.set(px, py, pz);
    if (typeof snap.fov === "number" && isFinite(snap.fov))
      camera.fov = snap.fov;
    camera.updateProjectionMatrix();
    if (controls?.target) {
      controls.target.set(tx, ty, tz);
      controls.update?.();
    }
  } catch {}
}

function getCameraSnapshot(camera, controls) {
  if (!camera) return null;
  try {
    return {
      pos: [camera.position.x, camera.position.y, camera.position.z],
      target: controls?.target
        ? [controls.target.x, controls.target.y, controls.target.z]
        : [0, 0, 0],
      fov: camera.fov,
    };
  } catch {
    return null;
  }
}

function getDefaultCamSnap(sex) {
  // ✅ TUNE THESE TWO ONLY
  // target.x: + moves the model LEFT on screen, - moves it RIGHT
  // target.y: lower value → model rises in frame
  // pos.z:   smaller value → model appears larger
  if (sex === "male") {
    return { pos: [0, 1.25, 3], target: [0.0, 1, 0.0], fov: 35 };
  }
  // Female framing tune: after adding new pose morphs, her mesh sat
  // low and small in frame. Tighter distance + lower target Y brings
  // her back to centred + larger. Adjust if the re-exported mesh
  // shifts her Y again.
  return { pos: [0, 1.0, 2], target: [0, 0.85, 0.0], fov: 35 };
}

/** ✅ Pivot fix: recenter OrbitControls.target to model bounds center
 *  while keeping framing (camera moves by same delta)
 */
function recenterOrbitPivotToRoot(
  root,
  camera,
  controls,
  offsets = { x: 0, y: 0, z: 0 }
) {
  if (!root || !camera || !controls || !controls.target) return;

  const box = new THREE.Box3().setFromObject(root);
  const center = new THREE.Vector3();
  box.getCenter(center);

  if (!isFinite(center.x) || !isFinite(center.y) || !isFinite(center.z)) return;

  const newTarget = center
    .clone()
    .add(new THREE.Vector3(offsets.x, offsets.y, offsets.z));
  const delta = newTarget.clone().sub(controls.target);

  // move camera by same delta so the image doesn't jump
  camera.position.add(delta);
  controls.target.copy(newTarget);
  controls.update?.();
}

/* simple global registry so UI panels can find 3D roots */
function registerTattooRoot(root, slot = 0) {
  if (!root) return;
  const w = window;
  w.__tattoo_roots = w.__tattoo_roots || [];
  w.__tattoo_roots[slot] = root;
}

/* per-model auto-home registry (was referenced but missing in your paste) */
function setAutoHome(modelKey, snap) {
  try {
    window.__tt_auto_home = window.__tt_auto_home || {};
    window.__tt_auto_home[modelKey] = snap || null;
  } catch {}
}
function getAutoHome(modelKey) {
  try {
    return window.__tt_auto_home ? window.__tt_auto_home[modelKey] : null;
  } catch {
    return null;
  }
}

function centerOrbitPivotOnObject(root, controls, { yBias = 0 } = {}) {
  if (!root || !controls) return;

  // make sure world matrices are correct
  root.updateWorldMatrix?.(true, true);

  const box = new THREE.Box3().setFromObject(root);
  const center = new THREE.Vector3();
  box.getCenter(center);

  // optional: aim a bit higher/lower than pure bbox center
  center.y += yBias;

  controls.target.copy(center);
  controls.update?.();

  // DEBUG (leave this in for one run)
  console.log(
    "[pivot] bbox center",
    center.toArray(),
    "box",
    box.min.toArray(),
    box.max.toArray()
  );
}

/* ============ FitOnModelSwitch (respects shared cam lock) ============ */
function FitOnModelSwitch({ ...props }) {
  const { camera } = useThree();
  const fittedFor = React.useRef(null);
  const [retryTick, setRetryTick] = React.useState(0);

  const shouldBlock =
    props.blockFit ||
    !props.modelKey ||
    performance.now() < (window.__tt_cam_lock_until || 0) ||
    !!window.__tt_lock_cam;

  React.useEffect(() => {
    if (shouldBlock) return;

    const bump = () => setRetryTick((t) => t + 1);
    window.addEventListener("tt:scene-ready", bump);
    const t1 = setTimeout(bump, 150);
    const t2 = setTimeout(bump, 450);
    const t3 = setTimeout(bump, 900);

    return () => {
      window.removeEventListener("tt:scene-ready", bump);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [props.modelKey, shouldBlock]);

  React.useEffect(() => {
    if (shouldBlock) return;

    // ...your existing fit logic here...
  }, [
    camera,
    props.controlsRef,
    props.fitRefs,
    props.modelKey,
    props.blockFit,
    props.pad,
    props.biasY,
    props.targetYOffset,
    props.camYOffset,
    props.targetXOffset,
    props.camXOffset,
    retryTick,
    shouldBlock,
  ]);

  return null;
}

/* Freeze camera.aspect after first measure */
function FreezeAspect() {
  const { camera, size } = useThree();
  const locked = useRef(null);
  useEffect(() => {
    const cur = size.width / Math.max(1, size.height);
    if (locked.current == null) {
      locked.current = cur;
      camera.aspect = cur;
      camera.updateProjectionMatrix();
    } else if (camera.aspect !== locked.current) {
      camera.aspect = locked.current;
      camera.updateProjectionMatrix();
    }
  }, [camera, size]);
  return null;
}

function CameraLock({ cameraRef, controlsRef }) {
  const { camera } = useThree();

  useEffect(() => {
    // ✅ always point the ref at the live r3f camera
    cameraRef.current = camera;

    let raf = 0;

    const loop = () => {
      const lock = window.__tt_lock_cam;
      if (window.__tt_cam_gesture) {
        raf = requestAnimationFrame(loop);
        return;
      }

      if (lock && performance.now() < lock.until) {
        try {
          applyCameraSnapshot(
            cameraRef.current,
            controlsRef.current,
            lock.snap
          );
        } catch {}
        raf = requestAnimationFrame(loop);
        return;
      }

      // lock window over
      try {
        window.__tt_lock_cam = null;
      } catch {}
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // controlsRef/cameraRef objects are stable; camera changes if R3F replaces it
  }, [camera, cameraRef, controlsRef]);

  return null;
}

/* ---------------- Color helpers (bridges to GuidePaint) ---------------- */

function toHex(n) {
  const clamped = Math.max(0, Math.min(255, Math.round(n)));
  return clamped.toString(16).padStart(2, "0");
}

function parseColorLike(c) {
  if (!c) return null;

  if (typeof c === "string" && /^#([0-9a-f]{6}|[0-9a-f]{3})$/i.test(c)) {
    if (c.length === 4) {
      const r = c[1],
        g = c[2],
        b = c[3];
      return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
    }
    return c.toLowerCase();
  }

  if (typeof c === "string") {
    const m = c.match(
      /rgba?\s*\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)/i
    );
    if (m) {
      const r = Number(m[1]),
        g = Number(m[2]),
        b = Number(m[3]);
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    }
  }

  if (Array.isArray(c) && c.length >= 3) {
    const [r, g, b] = c;
    const scale = r <= 1 || g <= 1 || b <= 1 ? 255 : 1;
    return `#${toHex(r * scale)}${toHex(g * scale)}${toHex(b * scale)}`;
  }

  if (typeof c === "object" && c) {
    const { r, g, b } = c;
    if (
      typeof r === "number" &&
      typeof g === "number" &&
      typeof b === "number"
    ) {
      const scale = r <= 1 || g <= 1 || b <= 1 ? 255 : 1;
      return `#${toHex(r * scale)}${toHex(g * scale)}${toHex(b * scale)}`;
    }
    if (c.isColor && typeof c.getHex === "function") {
      const hex = c.getHex();
      return `#${hex.toString(16).padStart(6, "0")}`;
    }
  }

  return null;
}

function readGPHex() {
  const gp = guidePaintRef.current;
  if (!gp) return null;

  if (typeof gp.getColor === "function") {
    const got = gp.getColor();
    const hx = parseColorLike(got);
    if (hx) return hx;
  }

  const sources = [gp.color, gp.brushColor, gp.paintColor];
  for (const s of sources) {
    const hx = parseColorLike(s);
    if (hx) return hx;
  }

  return null;
}

/* -------- Overlay: toolbar + morphs -------- */
function OverlayControls({ refs, showMorph = true, controlsRef }) {
  const portalHostRef = React.useRef(null);
  const [targets, setTargets] = useState([]);
  const isPoseMorph = (name) => /^POSE__/i.test(name);
  const [collapsed, setCollapsed] = useState(true);
  const [brushSize, setBrushSize] = React.useState(
    () => guidePaintRef.current?.getBrush?.() ?? 6
  );
  const [paintOn, setPaintOn] = useState(
    () => !!guidePaintRef.current?.enabled
  );
  const [symActive, setSymActive] = useState(
    () => !!guidePaintRef.current?.symmetry
  );

  const bodyImportInputRef = React.useRef(null);
  const { session } = useAuth();

  async function importBodyFromFile(file) {
    try {
      if (!file) return;

      if (!/\.json$/i.test(file.name) && file.type !== "application/json") {
        showToast("Please choose a .json body file");
        return;
      }

      const text = await file.text();
      const json = JSON.parse(text);

      const { applyBodyContext, openLeft } = await import("../tattoo/state");
      openLeft?.();
      const ok = applyBodyContext(json, { allowSex: true });

      showToast(ok ? "Imported physique ✅" : "Could not import physique");
    } catch (e) {
      console.warn("[body-import] failed:", e);
      showToast("Could not import physique");
    }
  }

  React.useEffect(() => {
    const sync = (e) => {
      const n = e?.detail?.size ?? guidePaintRef.current?.getBrush?.();
      if (Number.isFinite(n)) setBrushSize(n);
    };
    window.addEventListener("guidepaint:brush", sync);
    setTimeout(sync, 0);
    return () => {
      window.removeEventListener("guidepaint:brush", sync);
    };
  }, []);

  useEffect(() => {
    const sync = (e) => {
      const on = e?.detail?.on ?? guidePaintRef.current?.enabled ?? false;
      setPaintOn(!!on);
      setSymActive(!!guidePaintRef.current?.symmetry);
    };
    window.addEventListener("guidepaint:toggle", sync);
    const t = setTimeout(sync, 0);
    return () => {
      window.removeEventListener("guidepaint:toggle", sync);
      clearTimeout(t);
    };
  }, []);

  const collect = useCallback(() => {
    try {
      const bestByName = new Map();

      const scoreMesh = (mesh) => {
        const name = (mesh?.name || "").toLowerCase();
        let score = 0;

        if (/(body|skin|torso|base|mesh)/i.test(name)) score += 1_000_000;

        if (
          /(garment|short|shorts|bikini|top|bra|panty|pants|cloth|hair)/i.test(
            name
          )
        )
          score -= 500_000;

        const vtx = mesh?.geometry?.attributes?.position?.count
          ? mesh.geometry.attributes.position.count
          : 0;

        score += vtx;
        return score;
      };

      for (const r of refs || []) {
        const api = r && r.current;
        if (!api || typeof api.getMorphTargets !== "function") continue;

        let arr = [];
        try {
          arr = api.getMorphTargets() || [];
        } catch {
          arr = [];
        }

        for (const t of arr) {
          const mesh = t?.mesh;
          const dict = mesh?.morphTargetDictionary;
          if (!mesh || !dict) continue;

          const names = Object.keys(dict);
          const meshScore = scoreMesh(mesh);

          for (const name of names) {
            if (isPoseMorph(name)) continue;
            const index = dict[name];
            if (!Number.isInteger(index)) continue;

            const cur = bestByName.get(name);
            if (!cur || meshScore > cur.score) {
              bestByName.set(name, { name, mesh, index, score: meshScore });
            }
          }
        }
      }

      const entries = Array.from(bestByName.values())
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((m) => ({ name: m.name, mesh: m.mesh, index: m.index }));

      setTargets(
        entries.length
          ? [
              {
                id: "deduped-morphs",
                name: "Body Morphs",
                mesh: null,
                entries,
              },
            ]
          : []
      );
    } catch {
      setTargets([]);
    }
  }, [refs]);

  useEffect(() => {
    const onNewSession = () => {
      // allow view re-apply for same modelKey (female->female)
      appliedViewOnceRef.current = false;

      // nuke any in-flight camera locks from the old session
      try {
        window.__tt_lock_cam = null;
        window.__tt_cam_lock_until = 0;
        window.__tt_cam_gesture = false;
      } catch {}

      // optional but helps iOS get out of “dolly mood” immediately
      if (isIOS) {
        setOrbitKey((k) => k + 1); // remount OrbitControls
      } else {
        resetOrbitTouchState();
        setOrbitEnabled(true);
      }
    };

    window.addEventListener("tt:new-session", onNewSession);
    return () => window.removeEventListener("tt:new-session", onNewSession);
  }, []);

  useEffect(() => {
    collect();
    const timers = [150, 350, 800].map((ms) => setTimeout(collect, ms));
    return () => timers.forEach(clearTimeout);
  }, [collect]);

  useEffect(() => {
    const recollect = () => collect();
    const rerender = () => setTargets((t) => t.slice());
    window.addEventListener("morph:changed", recollect);
    window.addEventListener("morph:collect", recollect);
    window.addEventListener("morph:changed", rerender);
    return () => {
      window.removeEventListener("morph:changed", recollect);
      window.removeEventListener("morph:collect", recollect);
      window.removeEventListener("morph:changed", rerender);
    };
  }, [collect]);

  const setInfluence = (mesh, idx, v) => {
    try {
      const arr = mesh?.morphTargetInfluences;
      if (!arr || idx < 0 || idx >= arr.length) return;
      arr[idx] = v;
      const mats = Array.isArray(mesh.material)
        ? mesh.material
        : [mesh.material];
      mats.forEach((m) => m && (m.needsUpdate = true));
      try {
        refs.forEach((r) => r?.current?.syncMorphsNow?.());
      } catch {}
      window.dispatchEvent(new CustomEvent("morph:changed"));
      try {
        const name = Object.keys(mesh.morphTargetDictionary || {}).find(
          (n) => mesh.morphTargetDictionary[n] === idx
        );
        if (name) {
          window.dispatchEvent(
            new CustomEvent("morph:set", { detail: { name, value: v } })
          );
        }
      } catch {}
    } catch {}
  };

  const togglePaint = () => {
    const gp = guidePaintRef.current;
    const next = !gp?.enabled;
    try {
      if (next) gp?.setMode?.("paint");
      gp?.setEnabled?.(next);
    } catch {}
    setPaintOn(next);
  };

  const toggleSym = () => {
    const gp = guidePaintRef.current;
    const next = !gp?.symmetry;
    try {
      gp?.setSymmetry?.(next);
    } catch {}
    setSymActive(next);
  };

  const snapTattoo = useSnapshot(tattoo);

  const [ropeOn, setRopeOn] = useState(() => {
    try {
      return localStorage.getItem("tt_rope_enabled") === "1";
    } catch {
      return true;
    }
  });

  return (
    <div
      className="overlay-dock"
      style={{
        position: "absolute",
        top: 12,
        left: 20,
        zIndex: 6,
        pointerEvents: "none",
      }}
    >
      <div
        className="overlay-toolbar"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "rgba(22,22,22,0.88)",
          border: "1px solid #2b2b2b",
          borderRadius: 10,
          padding: "6px 8px",
          color: "#e8e8e8",
          boxShadow: "0 6px 24px rgba(0,0,0,0.35)",
          pointerEvents: "auto",
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          className="tt-btn tt-icon"
          title={
            paintOn
              ? "Painting ON (tap to stop)"
              : "Painting OFF (tap to start)"
          }
          onClick={togglePaint}
          data-active={paintOn ? "true" : "false"}
          style={{
            padding: "6px 10px",
            lineHeight: 1,
            borderColor: paintOn ? "#6a9c6a" : undefined,
            color: paintOn ? "#b7ffb7" : undefined,
          }}
        >
          <DockIcon icon={Pencil} />
        </button>

        <button
          type="button"
          className={`tt-btn move-icon ${collapsed ? "" : "is-active"}`}
          title={collapsed ? "Expand controls" : "Collapse controls"}
          onClick={() => setCollapsed((c) => !c)}
        >
          <DockIcon icon={Dumbbell} />
        </button>

        <button
          type="button"
          className="tt-btn tt-icon"
          title="Symmetry (⇄)"
          onClick={toggleSym}
          data-active={symActive ? "true" : "false"}
          style={{ padding: "6px 10px", lineHeight: 1 }}
        >
          <DockIcon icon={FlipHorizontal} />
        </button>

        <button
          type="button"
          className="tt-btn tt-icon"
          onClick={() => guidePaintRef.current?.undo?.()}
          title="Undo"
          style={{ padding: "6px 10px", lineHeight: 1 }}
        >
          <DockIcon icon={RotateCcw} />
        </button>

        <button
          type="button"
          className="tt-btn tt-icon"
          onClick={() => guidePaintRef.current?.redo?.()}
          title="Redo"
          style={{ padding: "6px 10px", lineHeight: 1 }}
        >
          <DockIcon icon={RotateCw} />
        </button>

        <div ref={portalHostRef} style={{ display: "contents" }} />

        <OverlayPaintControlsPortal container={portalHostRef.current} />

        <div
          className="gp-brush"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginLeft: 6,
            paddingLeft: 8,
            borderLeft: "1px solid #2b2b2b",
            flex: "1 1 320px",
            minWidth: 260,
          }}
        >
          <span style={{ fontSize: 12, opacity: 0.8 }}>Brush</span>
          <input
            type="range"
            min={1}
            max={48}
            step={1}
            value={brushSize}
            onChange={(ev) => {
              const v = parseInt(ev.target.value, 10);
              setBrushSize(v);
              try {
                guidePaintRef.current?.setBrush?.(v);
              } catch {}
            }}
            style={{ width: 110, touchAction: "pan-y" }}
            aria-label="Brush size"
          />

          <span
            style={{
              minWidth: 28,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
              fontSize: 12,
              opacity: 0.9,
            }}
          >
            {brushSize}px
          </span>
          <button
            className="btn"
            onClick={(e) => {
              e.stopPropagation();
              const gp = guidePaintRef.current;
              if (!gp) return;

              const next = !gp.isRopeEnabled?.(); // use gp as source of truth
              gp.setRopeEnabled?.(next);

              setRopeOn(next); // ✅ forces label update

              console.log("[gp] rope enabled =", next);
            }}
          >
            {ropeOn ? "Smooth ✓" : "Smooth"}
          </button>
        </div>
      </div>

      {!collapsed && showMorph && (
        <div
          className="tt-panel morph-panel"
          style={{
            marginTop: 56,
            background: "rgba(22,22,22,0.88)",
            border: "1px solid #2b2b2b",
            borderRadius: 10,
            padding: 12,
            color: "#e8e8e8",
            boxShadow: "0 6px 24px rgba(0,0,0,0.35)",
            maxHeight: 540,
            overflow: "auto",
            maxWidth: 360,
            pointerEvents: "auto",
          }}
        >
          <div
            className="tt-section-title"
            style={{
              marginBottom: 8,
              fontSize: 12,
              color: "#bfbfbf",
              textTransform: "uppercase",
            }}
          >
            Body Morphs
          </div>

          {!!session && (
            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 10,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                className="tt-btn"
                onClick={() => bodyImportInputRef.current?.click()}
                title="Import a physique JSON into this project"
                style={{ padding: "6px 10px", fontSize: 12 }}
              >
                📥 Import body
              </button>

              <input
                ref={bodyImportInputRef}
                type="file"
                accept="application/json,.json"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  importBodyFromFile(f);
                }}
              />
            </div>
          )}

          {targets.length === 0 && (
            <div style={{ fontSize: 12, color: "#9a9a9a" }}>
              No morph targets found.
            </div>
          )}

          {targets.map((t) => (
            <div key={`morph:${t.id}`} className="morph-block">
              {t.entries.map((e) => {
                const cur = e.mesh?.morphTargetInfluences?.[e.index] ?? 0;
                return (
                  <label
                    key={`slider:${t.id}:${e.index}`}
                    className="morph-row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      margin: "8px 0",
                    }}
                  >
                    <span
                      className="morph-name"
                      style={{
                        flex: "0 0 44%",
                        fontSize: 12,
                        color: "#cfcfcf",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {(e.name || "").replace(/_/g, " ")}
                    </span>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.01}
                      value={cur}
                      onChange={(ev) =>
                        setInfluence(
                          e.mesh,
                          e.index,
                          parseFloat(ev.target.value)
                        )
                      }
                      style={{ flex: 1, touchAction: "pan-y" }}
                    />
                  </label>
                );
              })}
            </div>
          ))}

          <div
            style={{
              marginTop: 10,
              paddingTop: 10,
              borderTop: "1px solid #2b2b2b",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#e8e8e8" }}>
                Skin tone
              </div>
              <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>
                Pick a tone so the artist previews the tattoo on you.
              </div>
            </div>

            <input
              type="color"
              value={snapTattoo.skinColor || "#d19771"}
              onChange={(e) => setSkinColor(e.target.value)}
              title="Skin tone"
              style={{
                width: 34,
                height: 34,
                padding: 0,
                border: "1px solid #333",
                borderRadius: 8,
                background: "transparent",
                cursor: "pointer",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Thumb Pad ---------- */
function ThumbPaintTogglePad({ side = "right", size = 68, label = "Paint" }) {
  const [on, setOn] = React.useState(() => {
    const gp = guidePaintRef.current;
    if (!gp) return false;
    return !!(gp.enabled ?? gp.isOn ?? gp.painting ?? false);
  });

  // --- refs ---
  const onRef = React.useRef(on);
  React.useEffect(() => {
    onRef.current = on;
  }, [on]);

  const didStrokeRef = React.useRef(false);

  const isTouchLike = React.useCallback(() => {
    if (typeof window === "undefined") return false;
    return (
      "ontouchstart" in window ||
      (navigator.maxTouchPoints || 0) > 0 ||
      (window.matchMedia && window.matchMedia("(pointer: coarse)").matches)
    );
  }, []);

  const padRef = React.useRef();

  const pulsePad = React.useCallback(() => {
    if (!padRef.current) return;

    padRef.current.animate(
      [
        { transform: "scale(1)" },
        { transform: "scale(1.12)" },
        { transform: "scale(1)" },
      ],
      {
        duration: 110,
        easing: "ease-out",
      }
    );
  }, []);

  const setPaintEnabled = React.useCallback((next) => {
    const gp = guidePaintRef.current;
    if (!gp?.setEnabled) return;

    // optional: force paint mode when turning on
    if (next && gp.setMode) {
      try {
        gp.setMode("paint");
      } catch {}
    }

    try {
      gp.setEnabled(!!next);
    } catch {}

    // optional: immediate UI snap (fine to keep)
    setOn(!!gp.enabled);
  }, []);

  // Mark that a stroke happened
  React.useEffect(() => {
    const onStroke = () => {
      didStrokeRef.current = true;
    };
    window.addEventListener("guidepaint:stroke", onStroke);
    return () => window.removeEventListener("guidepaint:stroke", onStroke);
  }, []);

  // Auto-disable after lift (mobile/tablet only)
  React.useEffect(() => {
    const endStroke = () => {
      if (!isTouchLike()) return;
      if (!onRef.current) return;

      // only auto-disable if they actually drew
      if (!didStrokeRef.current) return;

      didStrokeRef.current = false;
      setPaintEnabled(false);
    };

    window.addEventListener("pointerup", endStroke, true);
    window.addEventListener("pointercancel", endStroke, true);
    window.addEventListener("touchend", endStroke, { passive: true });
    window.addEventListener("touchcancel", endStroke, { passive: true });

    return () => {
      window.removeEventListener("pointerup", endStroke, true);
      window.removeEventListener("pointercancel", endStroke, true);
      window.removeEventListener("touchend", endStroke);
      window.removeEventListener("touchcancel", endStroke);
    };
  }, [isTouchLike, setPaintEnabled]);

  React.useEffect(() => {
    const handler = () => {
      if (!isTouchLike()) return;
      pulsePad();
    };

    window.addEventListener("guidepaint:stroke", handler);
    return () => window.removeEventListener("guidepaint:stroke", handler);
  }, [pulsePad, isTouchLike]);

  const toggle = React.useCallback(() => {
    const gp = guidePaintRef.current;
    if (!gp) return;

    const current = !!(gp.enabled ?? gp.isOn ?? gp.painting ?? onRef.current);
    const next = !current;

    if (next) didStrokeRef.current = false;
    setPaintEnabled(next);

    console.log("[PAD-Toggle] painting:", next);
  }, [setPaintEnabled]);

  const style = {
    position: "absolute",
    zIndex: 12,
    bottom: "calc(14px + env(safe-area-inset-bottom, 0px))",
    [side === "left"
      ? "left"
      : "right"]: `calc(14px + env(safe-area-inset-${side}, 0px))`,
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: "9999px",
    background: on ? "rgba(60,140,60,0.25)" : "rgba(255,255,255,0.12)",
    border: `1px solid ${
      on ? "rgba(120,220,120,0.75)" : "rgba(255,255,255,0.22)"
    }`,
    boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
    display: "grid",
    placeItems: "center",
    color: on ? "#b7ffb7" : "#eaeaea",
    fontSize: 16,
    userSelect: "none",
    touchAction: "manipulation",
    pointerEvents: "auto",
    opacity: 0.95,
  };

  return (
    <button
      ref={padRef}
      aria-pressed={on ? "true" : "false"}
      aria-label={label}
      onClick={toggle}
      style={style}
      className="tt-thumb-pad"
    >
      {on ? "✍🏻" : "⬤"}
    </button>
  );
}

function CameraFollowLightRig({
  keyIntensity = 2.2,
  fillIntensity = 1.4,
  rimIntensity = 5.0,
  rimIntensity2 = 4.0,
}) {
  const rig = React.useRef();
  const { camera } = useThree();

  useFrame(() => {
    if (!rig.current) return;
    rig.current.position.copy(camera.position);
    rig.current.quaternion.copy(camera.quaternion);
  });

  return (
    <group ref={rig}>
      <directionalLight
        position={[2, 4, 2]}
        intensity={1.1}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-bias={-0.0004}
      />
      <directionalLight
        intensity={keyIntensity}
        color="#fff3dc"
        position={[-2.4, 1.6, 2.6]}
      />
      <directionalLight
        intensity={fillIntensity}
        color="#cfe4ff"
        position={[2.2, 0.6, 2.2]}
      />
      <directionalLight
        intensity={rimIntensity}
        color="#d6e9ff"
        position={[-4.2, 1.8, -6.0]}
      />
      <directionalLight
        intensity={rimIntensity2}
        color="#d6e9ff"
        position={[4.2, 1.8, -7.5]}
      />
    </group>
  );
}

function ForceFrame({ forceFrameRef, cameraRef, controlsRef }) {
  useFrame(() => {
    const s = forceFrameRef.current;
    if (window.__tt_shared_cam_applied) return;
    if (performance.now() < (window.__tt_cam_lock_until || 0)) return;
    if (window.__tt_lock_cam) return;
    if (window.__tt_cam_gesture) return;
    if (!s?.root) return;
    if (
      performance.now() < (window.__tt_cam_lock_until || 0) ||
      window.__tt_lock_cam
    )
      return;
    if (performance.now() > s.until) return;

    const cam = cameraRef.current;
    const ctrls = controlsRef.current;
    const root = s.root;
    if (!cam || !ctrls || !root) return;

    // ✅ Robust HR finder (doesn't rely on `sex` being in scope)
    let hr =
      root.getObjectByName?.("FemaleHR") || root.getObjectByName?.("MaleHR");

    if (!hr) {
      // fallback: name contains femalehr/malehr somewhere
      let best = null;
      let bestVtx = -1;

      root.traverse?.((o) => {
        if (!o?.isSkinnedMesh) return;
        const n = (o.name || "").toLowerCase();
        if (!n.includes("femalehr") && !n.includes("malehr")) return;

        const vtx = o.geometry?.attributes?.position?.count || 0;
        if (vtx > bestVtx) {
          bestVtx = vtx;
          best = o;
        }
      });

      hr = best;
    }

    const bodyMeshes = hr ? [hr] : [pickBestSkinnedMesh(root)];
    bodyMeshes.forEach((m) => m.updateWorldMatrix?.(true, true));

    const { box, size, center } = boundsOfMeshes(bodyMeshes);
    const maxDim = Math.max(size.x, size.y, size.z);

    // ✅ aim at chest-ish (this is the real torso math)
    const aim = center.clone();
    aim.y = box.min.y + size.y * 0.65;

    const fov = cam.fov * (Math.PI / 180);
    let dist = maxDim / 2 / Math.tan(fov / 2);
    dist *= 1.25;
    console.log("[CAMTRACE] ForceFrame stomping");
    // ✅ deterministic front view (+Z)
    cam.position.set(aim.x, aim.y + maxDim * 0.1, aim.z + dist);
    ctrls.target.copy(aim);
    ctrls.update?.();
  });

  return null;
}

function InvalidateOnEvents({ controlsRef }) {
  const { invalidate } = useThree();

  useEffect(() => {
    const kick = () => invalidate();

    // 🔥 paint texture updated
    window.addEventListener("guidepaint:stroke", kick);

    // 🔥 your existing orbit thumb event (you already dispatch this)
    window.addEventListener("tattoo:orbit-thumb", kick);

    // optional: anything else that should force a redraw
    window.addEventListener("tt:scene-ready", kick);
    window.addEventListener("morph:changed", kick);

    return () => {
      window.removeEventListener("guidepaint:stroke", kick);
      window.removeEventListener("tattoo:orbit-thumb", kick);
      window.removeEventListener("tt:scene-ready", kick);
      window.removeEventListener("morph:changed", kick);
    };
  }, [invalidate]);

  // Also invalidate directly off OrbitControls "change"
  useEffect(() => {
    const c = controlsRef?.current;
    if (!c) return;

    const onChange = () => invalidate();
    c.addEventListener("change", onChange);
    return () => c.removeEventListener("change", onChange);
  }, [controlsRef, invalidate]);

  return null;
}

/* ---------------- main ---------------- */

const PreviewPane = forwardRef(function PreviewPane(props, ref) {
  const {
    readOnly,
    onReady,
    showBackToStudio = false,
    onBackToStudio = () => {},
    onToggleDesignPanel = () => {}, // ✅ add
    leftOpen = false, // ✅ add
  } = props;
  const snap = useSnapshot(tattoo);
  const [mode, setMode] = React.useState("paint"); // "paint" | "morphs"
  const isEditMode = snap.decalMode === "drag" || snap.decalMode === "place";

  function isDecalModeActive() {
    return tattoo?.decalMode === "drag" || tattoo?.decalMode === "place";
  }

  function getSelectedLayerId() {
    // prefer editor state first
    return (
      snap.editingLayerId ??
      tattoo.editingLayerId ??
      window.tattoo?.editingLayerId ??
      null
    );
  }

  const decalDragRef = useRef({
    dragging: false,
    layerId: null,
    mesh: null,
  });
  const dragPreviewRef = useRef({
    active: false,
    layerId: null,
    tx: 0.5,
    ty: 0.5,
    faceIndex: null,
  });

  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepthRef = useRef(0);
  // --- fast drag preview (no rebuild during drag) ---

  const lastCommitAtRef = useRef(0);
  const lastPickAtRef = useRef(0);
  const lastHitRef = useRef(null);
  const pendingPointerRef = useRef({ x: 0, y: 0, has: false });
  const pendingUVRef = useRef(null);
  const moveRafRef = useRef(0);

  const lastMoveXYRef = useRef({ x: 0, y: 0, has: false });

  // tweak this (2–4 is usually good)
  const MIN_MOVE_PX = 8;
  const MIN_MOVE_PX2 = MIN_MOVE_PX * MIN_MOVE_PX;

  const COMMIT_MS_MOBILE = 60; // try 50ms (~20 fps commits)
  const COMMIT_MS_DESKTOP = 16; // ~60 fps commits

  const lastLayerWriteAtRef = React.useRef(0);
  const LAYER_WRITE_MS_TOUCH = 50; // 50ms = 20fps (try 33 for 30fps)

  // --- orbit suppression / bookkeeping (safe to keep even if unused elsewhere)
  const lastDecalUpAtRef = useRef(0);

  const onDecalWheelRef = useRef(null);

  const lastFallbackAtRef = useRef(0);
  const lastXYRef = useRef({ x: 0, y: 0, init: false });

  const lastDragUVRef = React.useRef(null); // { u, v, faceIndex }

  const surfaceRef = useRef(null);

  const didEditGestureRef = useRef(false);

  const needOrbitResetRef = useRef(false);

  const activePointerIdRef = useRef(null);

  const activePointerTypeRef = React.useRef(null);

  const suppressOrbitUntilNextDownRef = useRef(false);

  const touchCountRef = useRef(0);

  const [orbitKey, setOrbitKey] = useState(0);
  const pendingOrbitSnapRef = useRef(null);

  const pointersRef = useRef(new Map()); // pointerId -> {x,y}

  const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
  const angle = (a, b) => Math.atan2(b.y - a.y, b.x - a.x);

  const [glReady, setGlReady] = useState(0);
  const decalRayRef = React.useRef(new THREE.Raycaster());
  const decalNdcRef = React.useRef(new THREE.Vector2());

  const prevGpMapRef = React.useRef(null);

  const decalGrabRef = React.useRef({ du: 0, dv: 0, has: false });

  function getTwoPoints() {
    const arr = Array.from(pointersRef.current.values());
    return arr.length >= 2 ? [arr[0], arr[1]] : null;
  }

  const pinchRef = useRef({
    active: false,
    layerId: null,
    startDist: 0,
    startAngle: 0,
    startSX: 0,
    startSY: 0,
    startRot: 0,
  });

  const wheelSessionRef = useRef({
    layerId: null,
    lastAt: 0,
    scaleX: null,
    scaleY: null,
    rot: null,
  });

  const {
    isMobile = false,
    onOpenMorphs = null,
    onOpenSheet = null,
    // panelMode, setPanelMode if you passed them (optional)
  } = props;
  // ---------- DECAL PICKER (RAW) ----------

  function getDecalPickTargets(meshOverride = null) {
    if (meshOverride) return [meshOverride];

    // You said there are only ever 4 meshes:
    // MaleHR | MalePaintProxy | FemaleHR | FemalePaintProxy
    // Accept either exact names or refs you store globally.
    const out = [];

    // If you already keep references somewhere, use those.
    // Option A: you store them on window:
    const m = window.__tt_body_meshes || {};

    if (m.maleProxy) out.push(m.maleProxy);
    if (m.femaleProxy) out.push(m.femaleProxy);
    if (m.maleHR) out.push(m.maleHR);
    if (m.femaleHR) out.push(m.femaleHR);

    // Option B fallback: you store them as window vars by name:
    // (only if you actually do this)
    if (!out.length) {
      const mp = window.MalePaintProxy;
      const fp = window.FemalePaintProxy;
      const mh = window.MaleHR;
      const fh = window.FemaleHR;
      if (mp) out.push(mp);
      if (fp) out.push(fp);
      if (mh) out.push(mh);
      if (fh) out.push(fh);
    }

    return out.filter(Boolean);
  }

  function pickSupportedVideoMimeType() {
    if (typeof MediaRecorder === "undefined") return "";

    const candidates = [
      // Best for iPhone / Safari / Instagram workflow
      "video/mp4;codecs=h264",
      "video/mp4",

      // Good fallbacks for Chromium/Firefox
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8",
      "video/webm",
    ];

    for (const type of candidates) {
      try {
        if (MediaRecorder.isTypeSupported(type)) return type;
      } catch {}
    }

    return "";
  }

  function pickDecalUVRaw(clientX, clientY, meshOverride = null) {
    const camera = cameraRef.current;
    const canvas = canvasElRef.current; // ✅ from onCreated
    if (!camera || !canvas) return null;
    const targets = getDecalPickTargets(meshOverride);

    if (!camera || !canvas || !targets.length) return null;

    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const nx = (clientX - rect.left) / rect.width;
    const ny = (clientY - rect.top) / rect.height;

    const x = Math.max(0, Math.min(1, nx));
    const y = Math.max(0, Math.min(1, ny));

    const ndc = decalNdcRef.current;
    ndc.x = x * 2 - 1;
    ndc.y = -(y * 2 - 1);

    const rc = decalRaycasterRef.current;
    rc.setFromCamera(ndc, camera);

    const hits = rc.intersectObjects(targets, true);
    if (!hits.length) return null;

    const h = hits[0];
    if (!h.uv) return null;

    return { u: h.uv.x, v: h.uv.y, hit: h };
  }

  function handleDragEnter(e) {
    const types = Array.from(e.dataTransfer?.types || []);

    if (!types.includes("Files")) return;

    e.preventDefault();
    e.stopPropagation();

    dragDepthRef.current += 1;
    setIsDragOver(true);
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();

    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragOver(false);
    }
  }

  async function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();

    dragDepthRef.current = 0;
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer?.files || []);

    console.log(
      "[drop] files:",
      files.map((f) => ({
        name: f.name,
        type: f.type,
        size: f.size,
      }))
    );

    const file = files.find((f) => {
      if (f.type?.startsWith("image/")) return true;
      return /\.(png|jpe?g|webp)$/i.test(f.name || "");
    });

    if (!file) {
      console.warn("[drop] no image file found");
      return;
    }

    try {
      console.log("[drop] importing file:", file.name);

      await addDecalFromFile(file, {
        source: "upload",
      });

      console.log("[drop] import complete");
    } catch (err) {
      console.warn("[drop] failed to add decal", err);
    }
  }
  // ---------- /DECAL PICKER ----------

  function getLayerById(id) {
    // ✅ always prefer live proxy state for imperative input handlers
    const live = tattoo.layers || [];
    const found = live.find((L) => L?.id === id);
    if (found) return found;

    // fallback (rare, e.g. during boot)
    const snapList = snap.layers || [];
    return snapList.find((L) => L?.id === id) || null;
  }

  function readLayerUV(layer) {
    if (!layer) return { tx: 0.5, ty: 0.5, scale: 1 };

    // tx/ty might live in different shapes depending on your earlier refactors
    const tx =
      layer.tx ?? layer?.transform?.tx ?? layer?.x ?? layer?.pos?.x ?? 0.5;

    const ty =
      layer.ty ?? layer?.transform?.ty ?? layer?.y ?? layer?.pos?.y ?? 0.5;

    const scale = layer.scale ?? layer?.transform?.scale ?? layer?.s ?? 1;

    return {
      tx: Number.isFinite(tx) ? tx : 0.5,
      ty: Number.isFinite(ty) ? ty : 0.5,
      scale: Number.isFinite(scale) ? scale : 1,
    };
  }

  function resetGuidePaintPickState(reason = "") {
    const gp = guidePaintRef.current;
    if (!gp) return;

    // These are the “sticky” / continuity vars that can block picking on a new decal
    gp._lastUV = null;
    gp._lastHitPoint = null;
    gp._lastRayHit = null;
    gp._activeMesh = null;

    // Rope filter can also “hold” old coords
    gp._ropePt = null;
    gp._rawPt = null;

    // If you added these in your GP build, clear them too (safe if undefined)
    gp._pendingHit = null;

    // optional debug
    // console.log("[GP] reset pick state:", reason);
  }

  function getClientXY(e) {
    // pointer events should always have clientX/Y, but iOS sometimes gives zeros briefly
    const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const y = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    return { x, y };
  }

  function isPointerNearActiveDecal(hit, layer, pointerType = "mouse") {
    if (!hit || !layer) return false;

    const { tx, ty, scale } = readLayerUV(layer);

    const WRAP = (d) => ((((d + 0.5) % 1) + 1) % 1) - 0.5;

    // clamp scale so tiny decals don't make tol explode
    const s = Math.max(0.15, Math.min(3.0, Number(scale ?? 1)));

    // base radius in UV (tune this)
    let tol = 0.1;

    // smaller decal => easier to grab (finger-sized)
    tol *= 1 / Math.sqrt(s);

    // extra forgiveness on touch
    if (pointerType === "touch") tol *= 1.25;

    // clamp final radius
    tol = Math.max(0.06, Math.min(0.18, tol));

    const du = WRAP(hit.u - tx);
    const dv = WRAP(hit.v - ty); // if V shouldn't wrap, change this to (hit.v - ty)

    return du * du + dv * dv <= tol * tol;
  }

  function sanitizeViewState(v, sex) {
    const def = getDefaultCamSnap(sex);

    if (!v || !Array.isArray(v.pos) || !Array.isArray(v.target)) return def;

    const [px, py, pz] = v.pos;
    const [tx, ty, tz] = v.target;

    const fovIn = v.fov ?? def.fov ?? 35;

    const okNums = [px, py, pz, tx, ty, tz, fovIn].every((n) =>
      Number.isFinite(n)
    );

    if (!okNums) return def;

    // camera-target distance sanity
    const dx = px - tx;
    const dy = py - ty;
    const dz = pz - tz;
    const dist = Math.hypot(dx, dy, dz);

    // 1) obviously broken snaps
    // too close (inside body) or insanely far
    if (dist < 0.35 || dist > 12) return def;

    // camera underground-ish or target absurdly low
    if (py < -0.2 || ty < -1.0) return def;

    // 2) “knees-down” heuristic for fullbody projects
    // (these thresholds match your bug without killing reasonable views)
    const kneesDown = (py < 0.55 && ty < 0.15) || ty < -0.25;
    if (kneesDown) return def;

    // Clamp FOV
    const fov = Math.min(60, Math.max(20, fovIn));

    return { pos: [px, py, pz], target: [tx, ty, tz], fov };
  }

  function getLayerTransform(layer) {
    const t = layer?.transform || { tx: 0.5, ty: 0.5, scale: 0.5, rot: 0 };
    const base = t.scale ?? 0.5;

    // ✅ PREVIEW OVERRIDE (dragging): do NOT write to state, only read
    const d = window.__tt_decal_drag;
    const dragTx = d?.active && d.layerId === layer?.id ? d.tx : undefined;
    const dragTy = d?.active && d.layerId === layer?.id ? d.ty : undefined;

    return {
      ...t,
      scaleX: t.scaleX ?? base,
      scaleY: t.scaleY ?? base,
      rot: t.rot ?? 0,
      tx: dragTx ?? t.tx ?? 0.5, // ✅ use preview tx if present
      ty: dragTy ?? t.ty ?? 0.5, // ✅ use preview ty if present
    };
  }

  function patchLayerTransform(layerId, patch) {
    console.count("[drag] layer transform write");
    const layer = getLayerById(layerId);
    if (!layer) return;

    const T = getLayerTransform(layer);
    const next = { ...T, ...patch };

    // Keep legacy `scale` in sync so older code still behaves
    if (patch.scaleX != null || patch.scaleY != null) {
      const avg = (next.scaleX + next.scaleY) * 0.5;
      next.scale = avg;
    }

    setLayerProp(layerId, "transform", next);

    // Make sure the preview actually updates this frame
    try {
      window.dispatchEvent(new Event("tt:decal-drag"));
    } catch {}
  }

  function onDecalPointerDown(e) {
    const activeLayerId = getSelectedLayerId?.();
    if (!activeLayerId) return;

    if (pinchRef.current.active) return;
    if (!(tattoo?.decalMode === "drag" || tattoo?.decalMode === "place"))
      return;

    // touch: if another finger is already down, let pinch system handle it
    if (e.pointerType === "touch" && touchCountRef.current >= 1) return;

    // if a different layer was previously latched, reset BEFORE starting a new drag
    if (
      window.__tt_decal_drag?.layerId &&
      window.__tt_decal_drag.layerId !== activeLayerId
    ) {
      ttResetDecalDragState({ forceHigh: false });
    }

    const layer = getLayerById(activeLayerId);
    const gp = guidePaintRef.current;
    if (!gp?.pickUVAtClientXY) return;

    const { x, y } = getClientXY(e);
    const pickMesh = window.__tt_decal_pick_mesh || null;
    const hit = gp.pickUVAtClientXY(x, y, pickMesh);
    if (!hit) return;

    // only drag if press is near the decal (prevents “jump then drag”)
    if (!isPointerNearActiveDecal(hit, layer, e.pointerType)) {
      if (e.pointerType === "touch") resetOrbitTouchState();
      setOrbitEnabled(true);
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    decalWheelActiveRef.current = true;
    setOrbitEnabled(false);

    // IMPORTANT: reset “grab offset” at drag start (this fixes pickup-point weirdness)
    decalGrabRef.current.has = false;
    decalGrabRef.current.du = 0;
    decalGrabRef.current.dv = 0;

    // reset throttles / last hit
    lastCommitAtRef.current = 0;
    lastHitRef.current = null;
    lastMoveXYRef.current.has = false;

    // begin drag
    decalDragRef.current.dragging = true;
    // compute grab offset so decal doesn't jump
    const T = getLayerTransform(layer) || layer.transform || {};
    const curU = Number(T.tx ?? layer.tx ?? 0.5);
    const curV = Number(T.ty ?? layer.ty ?? 0.5);

    decalGrabRef.current.has = true;
    decalGrabRef.current.du = curU - hit.u;
    decalGrabRef.current.dv = curV - hit.v;
    decalDragRef.current.layerId = activeLayerId;
    decalDragRef.current.mesh = hit.hit?.object || null;

    // capture pointer on the surface
    try {
      e.currentTarget?.setPointerCapture?.(e.pointerId);
      window.__tt_decal_drag_pointerId = e.pointerId;
      activePointerIdRef.current = e.pointerId;
      activePointerTypeRef.current = e.pointerType || null;
    } catch {}

    window.__tt_decal_drag_active = true;

    // low quality while dragging
    tattoo.renderQuality = "drag";
    tattoo.renderQualityTick = (tattoo.renderQualityTick || 0) + 1;

    window.__tt_pause_autosave = true;
    window.__tt_pause_autosave_reason = "decal-drag";
    try {
      window.dispatchEvent(new Event("tt:autosave-pause"));
    } catch {}
  }

  function onDecalWheel(e) {
    // ✅ NEW: only hijack wheel when user is explicitly asking for decal ops
    // No modifiers => let OrbitControls dolly normally
    const wantScale = e.shiftKey; // Shift = scale
    const wantRotate = e.altKey || e.metaKey; // Alt (or Cmd) = rotate
    if (!wantScale && !wantRotate) return;

    // keep the rest of your existing logic
    const layerId =
      decalDragRef.current.layerId || getSelectedLayerId?.() || null;
    if (!layerId) return;
    if (!isDecalModeActive()) return;

    const layer = getLayerById(layerId);
    if (!layer) return;

    // ✅ only prevent default AFTER we decide to hijack
    e.preventDefault();
    e.stopPropagation();

    const d = (Math.abs(e.deltaY) > 0 ? e.deltaY : e.deltaX) || 0;
    if (!d) return;

    // --- wheel session (prevents "snap back" to old scale) ---
    const now = performance.now();
    const S = wheelSessionRef.current;

    // reset session if idle or changed layer
    const idle = now - (S.lastAt || 0) > 220;
    if (idle || S.layerId !== layerId) {
      const T = getLayerTransform(layer);
      S.layerId = layerId;
      S.scaleX = Number(T.scaleX ?? T.scale ?? 0.5);
      S.scaleY = Number(T.scaleY ?? T.scale ?? 0.5);
      S.rot = Number(T.rot ?? 0);
    }
    S.lastAt = now;

    // tune sensitivity (trackpad vs wheel)
    const k = 0.0015;

    if (wantRotate && !wantScale) {
      // ROTATE
      S.rot = Number(S.rot ?? 0) + -d * 0.002;
      patchLayerTransform(layerId, { rot: S.rot });
      return;
    }

    if (wantScale) {
      // SCALE (uniform)
      const cur = Number(S.scaleX ?? 0.5);
      const mult = Math.exp(-d * k);
      const next = Math.min(6, Math.max(0.05, cur * mult));

      S.scaleX = next;
      S.scaleY = next;

      patchLayerTransform(layerId, { scaleX: next, scaleY: next });
      return;
    }
  }

  onDecalWheelRef.current = onDecalWheel;

  {
    /* 
  useEffect(() => {
    // iOS Safari only: prevent browser-level pinch gestures
    if (!isIOS) return;

    const block = (e) => {
      const pinching = pinchRef.current?.active;
      const dragging = decalDragRef.current?.dragging;

      // Only block Safari gesture events while OUR gesture is active
      if (isEditMode && (pinching || dragging)) {
        e.preventDefault();
      }
    };

    document.addEventListener("gesturestart", block, { passive: false });
    document.addEventListener("gesturechange", block, { passive: false });
    document.addEventListener("gestureend", block, { passive: false });

    return () => {
      document.removeEventListener("gesturestart", block);
      document.removeEventListener("gesturechange", block);
      document.removeEventListener("gestureend", block);
    };
  }, [isEditMode]);

  */
  }

  function onDecalPointerMove(e) {
    if (pinchRef.current.active) return;
    if (!decalDragRef.current.dragging) return;

    e.preventDefault();
    e.stopPropagation();

    const pointerType = e.pointerType; // ✅ capture now (don’t use e inside rAF)
    const { x, y } = getClientXY(e);

    const lm = lastMoveXYRef.current;
    if (lm.has) {
      const dx = x - lm.x;
      const dy = y - lm.y;
      if (dx * dx + dy * dy < MIN_MOVE_PX2) return;
    }
    lm.x = x;
    lm.y = y;
    lm.has = true;

    pendingPointerRef.current = { x, y, has: true };
    if (moveRafRef.current) return;

    moveRafRef.current = requestAnimationFrame(() => {
      try {
        const { layerId } = decalDragRef.current;
        if (!layerId) return;

        const p = pendingPointerRef.current;
        if (!p?.has) return;

        const pickMesh =
          decalDragRef.current.mesh || window.__tt_decal_pick_mesh || null;

        let hit = pickDecalUVRaw(p.x, p.y, pickMesh);
        if (!hit) hit = pickDecalUVRaw(p.x, p.y, null);

        console.log(
          "[decal-pick] using mesh =",
          pickMesh?.name || "(none)",
          "hit=",
          !!hit
        );

        // reuse last hit briefly if needed
        if (!hit && lastHitRef.current) hit = lastHitRef.current;

        if (hit) {
          lastHitRef.current = hit;

          const faceIndex = hit.hit?.faceIndex ?? null;
          const meshObj = hit.hit?.object ?? null;
          const point = hit.hit?.point ?? null;

          if (meshObj) decalDragRef.current.mesh = meshObj;

          // ✅ compute final decal position ONCE (grab offset applied)
          let u = hit.u;
          let v = hit.v;

          if (decalGrabRef.current.has) {
            u = hit.u + decalGrabRef.current.du;
            v = hit.v + decalGrabRef.current.dv;
          }

          u = Math.max(0, Math.min(1, u));
          v = Math.max(0, Math.min(1, v));

          // ✅ pending + lastDrag should store the FINAL u/v (not raw hit.u/v)
          pendingUVRef.current = {
            layerId,
            u,
            v,
            faceIndex,
            meshObj,
            hitWorld: point ? [point.x, point.y, point.z] : null,
          };

          lastDragUVRef.current = { u, v, faceIndex };

          const now2 = performance.now();
          const isTouch = pointerType === "touch"; // ✅ use captured
          const minMs = isTouch ? COMMIT_MS_MOBILE : COMMIT_MS_DESKTOP;

          if (now2 - lastCommitAtRef.current >= minMs) {
            lastCommitAtRef.current = now2;

            fastSetLayerTxTy(layerId, u, v);
          }

          try {
            window.dispatchEvent(new Event("tt:decal-drag"));
          } catch {}
        }
      } finally {
        moveRafRef.current = 0; // ✅ always clear
        pendingPointerRef.current.has = false;
      }
    });
  }

  function onDecalPointerUp(e) {
    if (!decalDragRef.current.dragging) return;

    if (e.pointerType === "touch") {
      resetOrbitTouchState();
    }

    try {
      e.preventDefault();
      e.stopPropagation();
    } catch {}

    decalWheelActiveRef.current = false;

    // --- commit final UV (ONE commit) ---
    const p = pendingUVRef.current;
    if (p?.layerId && typeof p.u === "number" && typeof p.v === "number") {
      patchLayerTransform(p.layerId, { tx: p.u, ty: p.v });
      setLayerProp(p.layerId, "anchor", {
        u: p.u,
        v: p.v,
        faceIndex: p.faceIndex ?? null,
      });

      try {
        wheelSessionRef.current.layerId = null;
        wheelSessionRef.current.scaleX = null;
        wheelSessionRef.current.scaleY = null;
        wheelSessionRef.current.rot = null;
      } catch {}
    }

    // --- END DRAG STATE (do this BEFORE rebuild) ---
    ttResetDecalDragState({ forceHigh: false });

    decalGrabRef.current.has = false;
    suppressOrbitUntilNextDownRef.current = false;
    lastDecalUpAtRef.current = performance.now();

    // ✅ tell PartModel/pipeline to rebuild once
    try {
      window.dispatchEvent(new Event("tt:decal-drop"));
    } catch {}

    // snap back to crisp once after drop
    tattoo.renderQuality = "high";
    tattoo.renderQualityTick = (tattoo.renderQualityTick || 0) + 1;

    // --- resume autosave after drag ---
    window.__tt_pause_autosave = false;
    window.__tt_pause_autosave_reason = null;

    try {
      window.dispatchEvent(new Event("tt:autosave-flush"));
    } catch {}
    try {
      window.dispatchEvent(new Event("tt:autosave-resume"));
    } catch {}

    // reset tiny-move filter for next drag
    lastMoveXYRef.current.has = false;

    softRecoverOrbitAfterEdit("decal-up");
  }

  // new surface handlers //

  function hardBlockEvent(e) {
    // Prevent OrbitControls' native listeners from ever seeing this gesture
    try {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent?.stopImmediatePropagation?.();
    } catch {}
  }
  lastMoveXYRef.current.has = false;
  // Decide if this single-finger press is “on the decal” (so we should take over)
  function isPointerDownOnActiveDecal(e) {
    try {
      const layerId = getSelectedLayerId?.();
      if (!layerId) return false;

      const layer = getLayerById(layerId);
      if (!layer) return false;

      const gp = guidePaintRef.current;
      if (!gp?.pickUVAtClientXY) return false;

      const { x, y } = getClientXY(e);
      const hit = gp.pickUVAtClientXY(x, y, null);
      if (!hit) return false;

      return isPointerNearActiveDecal(hit, layer);
    } catch {
      return false;
    }
  }

  function onSurfacePointerDownCapture(e) {
    if (!isEditMode) return;

    // Track pointers early (capture phase)
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // If a second finger arrives, we are pinching => block OrbitControls BEFORE it starts
    if (pointersRef.current.size === 2) {
      hardBlockEvent(e);
      onSurfacePointerDown(e); // your existing pinch start logic
      return;
    }
    didEditGestureRef.current = true;
    // Single finger: only take over if they’re grabbing the active decal.
    // Otherwise, LET OrbitControls handle orbit fully (do NOT block, do NOT capture).
    const onDecal = isPointerDownOnActiveDecal(e);

    if (onDecal) {
      hardBlockEvent(e);
      onSurfacePointerDown(e); // your existing drag start logic
    } else {
      // Allow orbit — important: OrbitControls must see down/move/up sequence itself
      setOrbitEnabled(true);
    }
  }

  function onSurfacePointerMoveCapture(e) {
    if (!isEditMode) return;

    // If we are handling pinch or decal drag, OrbitControls must never see move events
    if (pinchRef.current.active || decalDragRef.current.dragging) {
      hardBlockEvent(e);
      onSurfacePointerMove(e);
      return;
    }

    // otherwise: allow OrbitControls to receive this move (no-op here)
  }

  function onSurfacePointerUpCapture(e) {
    if (!isEditMode) return;

    // If we were handling pinch/drag, block OrbitControls from seeing the end event too
    if (pinchRef.current.active || decalDragRef.current.dragging) {
      hardBlockEvent(e);
      onSurfacePointerUp(e);
    }

    pointersRef.current.delete(e.pointerId);
  }

  function onSurfacePointerCancelCapture(e) {
    if (!isEditMode) return;

    if (pinchRef.current.active || decalDragRef.current.dragging) {
      hardBlockEvent(e);
      onSurfacePointerCancel(e);
    }

    pointersRef.current.delete(e.pointerId);
  }
  // new surface handlers //

  function onSurfacePointerDown(e) {
    if (!isEditMode) return;

    // capture so we keep getting moves
    try {
      e.currentTarget?.setPointerCapture?.(e.pointerId);
    } catch {}

    // If we now have 2 pointers, begin pinch and DO NOT start drag
    if (pointersRef.current.size === 2) {
      setOrbitEnabled(false);

      // ✅ kill any drag immediately (CANONICAL RESET)
      ttResetDecalDragState({ forceHigh: false });

      const two = getTwoPoints();
      if (!two) return;

      const layerId = getSelectedLayerId?.() || null;
      if (!layerId) return;

      const layer = getLayerById(layerId);
      if (!layer) return;

      const T = getLayerTransform(layer);

      pinchRef.current.active = true;
      pinchRef.current.layerId = layerId;
      pinchRef.current.startDist = dist(two[0], two[1]);
      pinchRef.current.startAngle = angle(two[0], two[1]);
      pinchRef.current.startSX = Number(T.scaleX ?? 0.5);
      pinchRef.current.startSY = Number(T.scaleY ?? 0.5);
      pinchRef.current.startRot = Number(T.rot ?? 0);

      // ✅ prevent browser gesture + stop bubbling
      e.preventDefault();
      e.stopPropagation();

      return;
    }

    // If pinch is active, never start drag
    if (pinchRef.current.active) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // otherwise, normal single-finger decal drag start
    onDecalPointerDown(e);
  }

  function onSurfacePointerMove(e) {
    if (!isEditMode) return;

    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    if (pinchRef.current.active) {
      const pr = pinchRef.current;
      const two = getTwoPoints();
      if (!two) return;

      const curDist = dist(two[0], two[1]);
      const curAngle = angle(two[0], two[1]);
      if (!pr.startDist) return;

      const mul = Math.min(6, Math.max(0.05, curDist / pr.startDist));
      const nextSx = Math.min(6, Math.max(0.05, pr.startSX * mul));
      const nextSy = Math.min(6, Math.max(0.05, pr.startSY * mul));
      const nextRot = pr.startRot + (curAngle - pr.startAngle);

      patchLayerTransform(pr.layerId, {
        scaleX: nextSx,
        scaleY: nextSy,
        rot: nextRot,
      });
      if (e.pointerType === "touch") didEditGestureRef.current = true;
      // keep orbit off while pinching
      setOrbitEnabled(false);

      e.preventDefault();
      e.stopPropagation();
      return;
    }

    onDecalPointerMove(e);
  }

  function onSurfacePointerUp(e) {
    if (!isEditMode) return;

    pointersRef.current.delete(e.pointerId);

    // end pinch when fewer than 2 pointers remain
    if (pinchRef.current.active && pointersRef.current.size < 2) {
      pinchRef.current.active = false;
      pinchRef.current.layerId = null;

      // IMPORTANT: orbit back on after reset
      needOrbitResetRef.current = true;
      requestAnimationFrame(() => {
        const c = controlsRef.current;
        if (c) {
          c.enabled = false;
          requestAnimationFrame(() => {
            resetOrbitTouchState();
            c.enabled = true;
            c.update?.();
          });
        }
        setOrbitEnabled(true);
        softRecoverOrbitAfterEdit("pinch-end");
      });
      return;
    }

    try {
      e.currentTarget?.releasePointerCapture?.(e.pointerId);
    } catch {}

    onDecalPointerUp(e);
  }

  function onSurfacePointerCancel(e) {
    if (!isEditMode) return;

    pointersRef.current.delete(e.pointerId);

    if (pinchRef.current.active && pointersRef.current.size < 2) {
      pinchRef.current.active = false;
      pinchRef.current.layerId = null;

      e.preventDefault?.();
      e.stopPropagation?.();

      try {
        e.currentTarget?.releasePointerCapture?.(e.pointerId);
      } catch {}

      // mark that OrbitControls needs a touch-state reset after the gesture fully ends
      needOrbitResetRef.current = true;

      // give Safari one frame to finish the pointer/touch sequence
      requestAnimationFrame(() => {
        // keep camera exactly where it is; just clear OrbitControls touch state
        resetOrbitTouchState();
        setOrbitEnabled(true);
      });

      return;
    }

    try {
      e.currentTarget?.releasePointerCapture?.(e.pointerId);
    } catch {}

    try {
      onDecalPointerUp(e);
    } catch {}
  }

  function ttResetDecalDragState({ forceHigh = false } = {}) {
    // release capture from the SAME element that captured (surface div)
    try {
      const dom = surfaceRef?.current;
      const pid = window.__tt_decal_drag_pointerId;
      if (dom && pid != null && activePointerIdRef.current === pid) {
        dom.releasePointerCapture?.(pid);
      }
    } catch {}

    try {
      window.__tt_decal_drag_active = false;
      window.__tt_decal_drag_pointerId = null;

      if (window.__tt_decal_drag) {
        window.__tt_decal_drag.active = false;
        window.__tt_decal_drag.layerId = null;
        window.__tt_decal_drag.hitWorld = null;
        window.__tt_decal_drag.tx = null;
        window.__tt_decal_drag.ty = null;
        window.__tt_decal_drag.url = null;
      }
    } catch {}

    // ✅ clear your local refs so nothing thinks it’s still dragging
    try {
      decalDragRef.current.dragging = false;
      decalDragRef.current.layerId = null;
      decalDragRef.current.mesh = null;

      pendingUVRef.current = null;
      lastHitRef.current = null;

      lastMoveXYRef.current.has = false;
      activePointerIdRef.current = null;
      activePointerTypeRef.current = null;

      // if you’re using the grab offset
      if (decalGrabRef?.current) decalGrabRef.current.has = false;
    } catch {}

    if (forceHigh) {
      tattoo.renderQuality = "high";
      tattoo.renderQualityTick = (tattoo.renderQualityTick || 0) + 1;
    }
  }

  function resetOrbitTouchState() {
    const c = controlsRef.current;
    if (!c) return;

    try {
      // These exist in three/examples OrbitControls (Drei wraps it but keeps internals)
      if (c._state !== undefined) c._state = -1; // STATE.NONE
      if (c._touchCurrent) c._touchCurrent = {}; // clear touches
      if (c._touchStart) c._touchStart = {}; // clear touches

      // Some versions use these names
      if (c._pointers) c._pointers.length = 0;
      if (c._pointerPositions) c._pointerPositions.clear?.();

      // Stop any inertia carry-over
      if (c.enableDamping) {
        // zero deltas if present
        if (c._sphericalDelta) c._sphericalDelta.set?.(0, 0, 0);
        if (c._panOffset) c._panOffset.set?.(0, 0, 0);
        if (c._scale !== undefined) c._scale = 1;
      }

      c.update?.();
    } catch (e) {
      console.warn("[orbit] resetTouchState failed", e);
    }
  }

  function softRecoverOrbitAfterEdit(reason = "edit-end") {
    if (!isIOS) return;

    // disable whatever exists *right now*
    try {
      const c0 = controlsRef.current;
      if (c0) c0.enabled = false;
    } catch {}

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // re-grab the LIVE instance
        const c = controlsRef.current;
        if (!c) return;

        resetOrbitTouchState();

        // IMPORTANT: clear any camera lock that would "fight" user input
        try {
          window.__tt_cam_gesture = false;
          window.__tt_lock_cam = null;
          window.__tt_cam_lock_until = 0;
        } catch {}

        c.enabled = true;
        c.update?.();

        // console.log("[orbit] softRecoverAfterEdit", reason, "enabled:", c.enabled);
      });
    });
  }

  function setOrbitEnabled(on) {
    const c = controlsRef.current;
    if (!c) return;
    const next = !!on;
    if (c.enabled === next) return; // ✅ prevents thrash flicker
    c.enabled = next;
    c.update?.();
  }

  const sex = snap.sex || "female";
  const view = "fullbody";
  const modelKey = `${sex}-fullbody`;
  const forceFrameRef = useRef({
    until: 0,
    root: null,
  });

  const [showLogo, setShowLogo] = useState(false);

  useEffect(() => {
    setShowLogo(false);
    const t = setTimeout(() => setShowLogo(true), 800); // after your ForceFrame window
    return () => clearTimeout(t);
  }, [modelKey]);

  // 🔎 DEBUG: detect who overwrites camera after we set it
  useEffect(() => {
    const cam = cameraRef.current;
    const ctrls = controlsRef.current;
    if (!cam || !ctrls) return;

    const tag = (label) => {
      try {
        console.log(`%c[CAMTRACE] ${label}`, "color:#7dd3fc", {
          pos: cam.position.toArray().map((n) => +n.toFixed(3)),
          target: ctrls.target.toArray().map((n) => +n.toFixed(3)),
          fov: cam.fov,
          lockUntil: window.__tt_cam_lock_until || 0,
          hasLock: !!window.__tt_lock_cam,
          pendingCam: !!window.__tt_pending_share?.cam,
        });
      } catch {}
    };

    // sample a few frames after mount / ready
    tag("mounted");
    const t1 = setTimeout(() => tag("t+50ms"), 50);
    const t2 = setTimeout(() => tag("t+200ms"), 200);
    const t3 = setTimeout(() => tag("t+700ms"), 700);
    const t4 = setTimeout(() => tag("t+1500ms"), 1500);

    // tag key events
    const onReady = () => tag("event tt:scene-ready");
    const onSetCam = (e) => tag("event tt:set-camera");
    const onStroke = () => tag("event guidepaint:stroke");

    window.addEventListener("tt:scene-ready", onReady);
    window.addEventListener("tt:set-camera", onSetCam);
    window.addEventListener("guidepaint:stroke", onStroke);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      window.removeEventListener("tt:scene-ready", onReady);
      window.removeEventListener("tt:set-camera", onSetCam);
      window.removeEventListener("guidepaint:stroke", onStroke);
    };
  }, [modelKey]);

  const DEFAULT_FIT =
    sex === "male"
      ? {
          pad: 1.12,
          biasY: -0.1,
          targetYOffset: 0,
          targetXOffset: 0.0,
          camYOffset: -0.1,
        }
      : {
          pad: 1.12,
          biasY: -0.1,
          targetYOffset: 0,
          targetXOffset: -0.18,
          camYOffset: 0.1,
        };

  const homeSnapRef = useRef(null);

  useEffect(() => {
    homeSnapRef.current = safeLoadHome(modelKey);
  }, [modelKey]);

  const homeCamRef = useRef(null);

  const consumedShareKeyRef = useRef(null);

  function getShareKey() {
    try {
      const hash = window.location.hash || "";
      // include pending share identity if you use it
      const pending = window.__tt_pending_share
        ? JSON.stringify(window.__tt_pending_share)
        : "";
      return hash + "|" + pending;
    } catch {
      return null;
    }
  }

  function easeTo(
    camera,
    controls,
    snap,
    ms = 260,
    { keepTarget = false } = {}
  ) {
    if (!camera || !controls || !snap) return;

    const fromPos = camera.position.clone();
    const fromTgt = controls.target.clone();
    const fromFov = camera.fov;

    const snapPos = new THREE.Vector3(...(snap.pos || [0, 1, 2.2]));
    const snapTgt = new THREE.Vector3(...(snap.target || [0, 0, 0]));
    const toFov = typeof snap.fov === "number" ? snap.fov : camera.fov;

    const toTgt = keepTarget ? fromTgt.clone() : snapTgt;

    // ✅ camera offset relative to snap target
    const rel = snapPos.clone().sub(snapTgt);
    const toPos = keepTarget ? toTgt.clone().add(rel) : snapPos;

    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    const tick = () => {
      const t = Math.min(1, (performance.now() - t0) / ms);
      const k = ease(t);

      camera.position.lerpVectors(fromPos, toPos, k);
      controls.target.lerpVectors(fromTgt, toTgt, k);
      camera.fov = fromFov + (toFov - fromFov) * k;

      camera.updateProjectionMatrix();
      controls.update?.();

      if (t < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  const partKey = "fullbody";

  const filteredLayers = useMemo(
    () =>
      (snap.layers || []).filter((L) => {
        if (!L) return false;
        if (L.visible === false) return false;
        if (L.sex && L.sex !== sex) return false;

        const pk = partKey;
        const partOk = L.part
          ? L.part === pk
          : Array.isArray(L.partKeys)
          ? L.partKeys.includes(pk)
          : true;
        return partOk;
      }),
    [snap.layers, sex, partKey]
  );

  const isTouch = useMediaQuery("(hover: none), (pointer: coarse)");
  const tattooKey = (snap.uvImage || "none").slice(0, 64);

  const controlsRef = useRef();
  const fullRef = useRef();
  const canvasElRef = useRef(null);
  const cameraRef = useRef(null);
  const glRef = useRef(null);
  const sceneRef = useRef(null);

  const sharedCamRef = useRef(false);
  const lastSharedCamRef = useRef(null);

  const decalWheelActiveRef = useRef(false); // true only while pointer is down on decal

  const [orbitTight, setOrbitTight] = useState(false);

  function getViewState() {
    const cam = cameraRef.current;
    const c = controlsRef.current;
    if (!cam || !c) return null;

    return {
      camPos: [cam.position.x, cam.position.y, cam.position.z],
      target: [c.target.x, c.target.y, c.target.z],
      fov: cam.fov,
      zoom: cam.zoom,
    };
  }

  const decalRaycasterRef = React.useRef(new THREE.Raycaster());
  function getPickTargets(meshOverride = null) {
    if (meshOverride) return [meshOverride];

    const m = window.__tt_body_meshes || {};

    // Prefer proxies first
    const targets = [];
    if (m.maleProxy) targets.push(m.maleProxy);
    if (m.femaleProxy) targets.push(m.femaleProxy);

    // fallback to HR if proxies missing
    if (!targets.length) {
      if (m.maleHR) targets.push(m.maleHR);
      if (m.femaleHR) targets.push(m.femaleHR);
    }
    return targets;
  }

  function SceneBinder({ sceneRef, glRef, cameraRef }) {
    const { scene, gl, camera } = useThree();

    useEffect(() => {
      sceneRef.current = scene;
      glRef.current = gl;
      cameraRef.current = camera;
    }, [scene, gl, camera, sceneRef, glRef, cameraRef]);

    return null;
  }

  function pickDecalUVRaw(clientX, clientY, meshOverride = null) {
    const camera = cameraRef.current;
    const canvas = canvasElRef.current; // MUST be the WebGL canvas element
    if (!camera || !canvas) return null;

    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;

    // clamp helps iOS “drift off canvas”
    const cx = Math.max(0, Math.min(1, x));
    const cy = Math.max(0, Math.min(1, y));

    const ndc = decalNdcRef.current;
    ndc.x = cx * 2 - 1;
    ndc.y = -(cy * 2 - 1);

    const rc = decalRaycasterRef.current;
    rc.setFromCamera(ndc, camera);

    const targets = getPickTargets(meshOverride);
    if (!targets.length) return null;

    const hits = rc.intersectObjects(targets, true);
    if (!hits.length) return null;

    // nearest hit
    const h = hits[0];
    if (!h.uv) return null;

    // If your pipeline uses FLIP_V, apply it here too
    const u = h.uv.x;
    const v = /* FLIP_V ? 1 - h.uv.y : */ h.uv.y;

    return { u, v, hit: h };
  }

  function applyViewState(v) {
    const cam = cameraRef.current;
    const c = controlsRef.current;
    if (!cam || !c || !v) return;

    const [cx, cy, cz] = v.camPos || [];
    const [tx, ty, tz] = v.target || [];

    if ([cx, cy, cz].every(Number.isFinite)) cam.position.set(cx, cy, cz);
    if ([tx, ty, tz].every(Number.isFinite)) c.target.set(tx, ty, tz);

    if (Number.isFinite(v.fov)) cam.fov = v.fov;
    if (Number.isFinite(v.zoom)) cam.zoom = v.zoom;

    cam.updateProjectionMatrix();
    c.update?.();
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  const appliedViewOnceRef = useRef(false);

  const dragActiveRef = useRef(false);

  const [studioLoading, setStudioLoading] = React.useState(true);

  React.useEffect(() => {
    // whenever model/sex changes, show loader again
    setStudioLoading(true);

    const done = () => {
      // let first stable frame render before hiding
      requestAnimationFrame(() => {
        setTimeout(() => setStudioLoading(false), 120);
      });
    };

    window.addEventListener("tt:scene-ready", done);
    return () => window.removeEventListener("tt:scene-ready", done);
  }, [sex, modelKey]);

  useEffect(() => {
    if (!isIOS) return;

    // after OrbitControls remounts, ref will point to new instance
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const snap = pendingOrbitSnapRef.current;
        if (!snap) return;

        const cam = cameraRef.current;
        const ctrls = controlsRef.current;
        if (!cam || !ctrls) return;

        applyCameraSnapshot(cam, ctrls, snap);
        pendingOrbitSnapRef.current = null;

        // ensure enabled
        ctrls.enabled = true;
        ctrls.update?.();
      });
    });
  }, [orbitKey]);

  useEffect(() => {
    appliedViewOnceRef.current = false;
  }, [modelKey]); // reset when switching male/female etc.

  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) return;

    const handler = (e) => onDecalWheelRef.current?.(e);

    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  useEffect(() => {
    const onReady = () => {
      if (appliedViewOnceRef.current) return;

      const cam = cameraRef.current;
      const ctrls = controlsRef.current;
      if (!cam || !ctrls) return;

      const pendingCam = window.__tt_pending_share?.cam;

      // ✅ Apply share cam if present, otherwise fall back to tattoo.viewState
      const cleaned = sanitizeViewState(pendingCam || tattoo.viewState, sex);

      console.log(
        "[CAMTRACE] tt:scene-ready applying",
        cleaned,
        "pendingCam=",
        !!pendingCam,
        "raw viewState=",
        tattoo.viewState
      );

      applyCameraSnapshot(cam, ctrls, cleaned);

      // ✅ If we used share cam, persist + clear it so nothing else overrides
      if (pendingCam) {
        tattoo.viewState = cleaned;
        try {
          window.__tt_pending_share.cam = null;
        } catch {}
      }

      // Lock briefly so fit/frame can’t stomp it right after
      window.__tt_cam_lock_until = performance.now() + 1200;

      appliedViewOnceRef.current = true;
    };

    window.addEventListener("tt:scene-ready", onReady);
    return () => window.removeEventListener("tt:scene-ready", onReady);
  }, [modelKey, sex]);

  const lastEditingLayerIdRef = useRef(null);

  useEffect(() => {
    const now = tattoo.editingLayerId;
    const was = lastEditingLayerIdRef.current;

    lastEditingLayerIdRef.current = now;

    if (tattoo.decalMode !== "drag") return;
    if (!now) return;

    // if you switched to a different layer while still in edit mode,
    // kill any latched pointer capture / active drag state
    if (was && now !== was) {
      // ✅ don't reset while dragging
      if (!decalDragRef.current?.dragging) {
        ttResetDecalDragState({ forceHigh: false });
      }
    }
  }, [tattoo.editingLayerId, tattoo.decalMode]);

  // ✅ Apply pending share cam AFTER scene is ready (so nothing overwrites it)
  useEffect(() => {
    let raf = 0;
    let tries = 0;

    const tick = () => {
      const pending = window.__tt_pending_share;
      const camSnap = pending?.cam;

      const cam = cameraRef.current;
      const ctrls = controlsRef.current;

      // need both camera + orbit controls to actually apply target
      if (!cam || !ctrls) {
        if (tries++ < 120) raf = requestAnimationFrame(tick);
        return;
      }

      if (!camSnap || !camSnap.pos || !camSnap.target) return;

      // ✅ apply it
      applyCameraSnapshot(cam, ctrls, camSnap);

      // ✅ make it the project's viewState too (so later code uses the right thing)
      try {
        tattoo.viewState = camSnap;
      } catch {}

      // ✅ lock briefly so Fit/ForceFrame/etc can’t stomp it
      try {
        window.__tt_shared_cam_applied = true;
        window.__tt_skip_fit_once = true;
        window.__tt_cam_lock_until = performance.now() + 2000;
        window.__tt_lock_cam = {
          snap: camSnap,
          until: window.__tt_cam_lock_until,
        };
      } catch {}

      // ✅ consume just the cam (keep focus/r if you want)
      try {
        window.__tt_pending_share = { ...pending, cam: null };
      } catch {}
    };

    const onSceneReady = () => {
      tries = 0;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("tt:scene-ready", onSceneReady);

    // also try once on mount in case scene-ready already fired
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("tt:scene-ready", onSceneReady);
      cancelAnimationFrame(raf);
    };
  }, [modelKey]);

  // ✅ Apply the "recent thumbnail view" when opening a recent project
  useEffect(() => {
    let tries = 0;
    let cancelled = false;

    const run = () => {
      if (cancelled) return;

      const vs = window.__tt_recent_viewSnap;
      if (!vs) return;

      const cam = cameraRef.current;
      const ctrls = controlsRef.current;

      // wait until both exist
      if (!cam || !ctrls) {
        if (tries++ < 60) requestAnimationFrame(run);
        return;
      }

      // IMPORTANT: if a share cam is being applied/locked, don't fight it
      if (
        performance.now() < (window.__tt_cam_lock_until || 0) ||
        window.__tt_lock_cam
      ) {
        if (tries++ < 60) requestAnimationFrame(run);
        return;
      }

      try {
        applyViewState(vs);

        // lock it briefly so Fit/ForceFrame doesn't immediately overwrite it
        window.__tt_cam_lock_until = performance.now() + 900;
        window.__tt_lock_cam = {
          snap: { pos: vs.camPos, target: vs.target, fov: vs.fov },
          until: window.__tt_cam_lock_until,
        };

        // consume
        window.__tt_recent_viewSnap = null;
      } catch (e) {
        console.warn("[PreviewPane] apply recent viewSnap failed", e);
      }
    };

    requestAnimationFrame(() => requestAnimationFrame(run));

    return () => {
      cancelled = true;
    };
  }, [modelKey]);

  // ✅ Persist camera into tattoo.viewState when orbit ends
  useEffect(() => {
    let ctrls = null;
    let raf = 0;

    const attach = () => {
      const cam = cameraRef.current;
      const c = controlsRef.current;

      if (!cam || !c) {
        raf = requestAnimationFrame(attach);
        return;
      }

      ctrls = c;

      const onEnd = () => {
        try {
          tattoo.viewState = {
            pos: [cam.position.x, cam.position.y, cam.position.z],
            target: [ctrls.target.x, ctrls.target.y, ctrls.target.z],
            fov: cam.fov,
            zoom: cam.zoom,
            at: Date.now(),
          };
          console.log("[CAM] saved viewState", tattoo.viewState);
        } catch {}

        try {
          markDirty("camera-end");
        } catch {}

        try {
          window.dispatchEvent(new Event("tt:autosave-flush"));
        } catch {}
      };

      ctrls.addEventListener("end", onEnd);

      // cleanup for this attach
      return () => {
        try {
          ctrls.removeEventListener("end", onEnd);
        } catch {}
      };
    };

    // run attach and capture its cleanup once attached
    let cleanup = null;
    const runner = () => {
      const maybeCleanup = attach();
      if (typeof maybeCleanup === "function") cleanup = maybeCleanup;
    };
    runner();

    return () => {
      cancelAnimationFrame(raf);
      if (cleanup) cleanup();
    };
  }, [orbitKey, modelKey]); // IMPORTANT: re-attach if OrbitControls remounts

  useEffect(() => {
    if (snap.leftOpen) {
      setOrbitTight(true);
      return;
    }
    setOrbitTight(true);
    const timeout = setTimeout(() => setOrbitTight(false), 800);
    return () => clearTimeout(timeout);
  }, [snap.leftOpen]);

  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls) return;

    if (orbitTight) {
      controls.minDistance = 1;
      controls.maxDistance = 10;
      controls.zoomSpeed = 0.7;
      controls.rotateSpeed = 0.75;
      controls.panSpeed = 0.4;
    } else {
      controls.minDistance = 0.02;
      controls.maxDistance = 999;
      controls.zoomSpeed = 1.0;
      controls.rotateSpeed = 1.0;
      controls.panSpeed = 0.8;
    }

    controls.update?.();
  }, [orbitTight]);

  useImperativeHandle(ref, () => ({
    async capturePng(opts = {}) {
      const gl = glRef.current;
      const camera = cameraRef.current;
      const scene = sceneRef.current;

      if (!gl || !gl.domElement || !camera || !scene) {
        console.warn("[PreviewPane] capturePng: missing gl/camera/scene");
        return null;
      }

      const srcCanvas = gl.domElement;

      const {
        width = null,
        height = null,
        quality = 0.92,
        format = "image/png",
        thumbnail = false,
        maxWidth = 480,
      } = opts || {};

      try {
        const prevSize = gl.getSize(new THREE.Vector2());
        const prevPixelRatio = gl.getPixelRatio();

        let targetWidth = srcCanvas.width;
        let targetHeight = srcCanvas.height;

        // thumbnail mode for recents/autosave
        if (thumbnail) {
          const scale = Math.min(1, maxWidth / srcCanvas.width);
          targetWidth = Math.round(srcCanvas.width * scale);
          targetHeight = Math.round(srcCanvas.height * scale);
        }

        // explicit export size overrides
        if (typeof width === "number" && width > 0) {
          targetWidth = Math.round(width);

          if (typeof height === "number" && height > 0) {
            targetHeight = Math.round(height);
          } else {
            const aspect = srcCanvas.height / srcCanvas.width;
            targetHeight = Math.round(targetWidth * aspect);
          }
        } else if (typeof height === "number" && height > 0) {
          targetHeight = Math.round(height);
          const aspect = srcCanvas.width / srcCanvas.height;
          targetWidth = Math.round(targetHeight * aspect);
        }

        // true high-res render
        gl.setPixelRatio(1);
        gl.setSize(targetWidth, targetHeight, false);

        if (camera.isPerspectiveCamera || camera.isOrthographicCamera) {
          camera.aspect = targetWidth / targetHeight;
          camera.updateProjectionMatrix();
        }

        gl.render(scene, camera);

        const dataUrl =
          format === "image/jpeg"
            ? gl.domElement.toDataURL("image/jpeg", quality)
            : gl.domElement.toDataURL("image/png");

        // restore renderer size
        gl.setPixelRatio(prevPixelRatio);
        gl.setSize(prevSize.x, prevSize.y, false);

        if (camera.isPerspectiveCamera || camera.isOrthographicCamera) {
          camera.aspect = prevSize.x / prevSize.y;
          camera.updateProjectionMatrix();
        }

        gl.render(scene, camera);

        console.log("[PreviewPane] capturePng", {
          src: `${srcCanvas.width}x${srcCanvas.height}`,
          out: `${targetWidth}x${targetHeight}`,
          format,
          thumbnail,
          length: dataUrl.length,
        });

        return dataUrl;
      } catch (e) {
        console.warn("[PreviewPane] capturePng failed", e);
        try {
          const dataUrl = srcCanvas.toDataURL("image/png");
          return dataUrl;
        } catch (err2) {
          console.error("[PreviewPane] capturePng failed completely", err2);
          return null;
        }
      }
    },

    async exportVideoSlowPush(opts = {}) {
      if (typeof MediaRecorder === "undefined") {
        console.warn(
          "[PreviewPane] MediaRecorder not available on this device/browser"
        );
        return null;
      }

      const gl = glRef.current;
      const camera = cameraRef.current;
      const controls = controlsRef.current;

      if (!gl || !gl.domElement || !camera || !controls) {
        console.warn(
          "[PreviewPane] exportVideoSlowPush: missing gl/camera/controls"
        );
        return null;
      }

      const {
        durationMs = 8500,
        fps = 30,
        preset = "push", // "push" | "sweepRight" | "sweepLeft"
        overlaySrc = "/TT_Reel_Overlay.png",
      } = opts;

      const srcCanvas = gl.domElement;

      const EXPORT_W = 1290;
      const EXPORT_H = 2154;

      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = EXPORT_W;
      exportCanvas.height = EXPORT_H;

      const ctx = exportCanvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      const overlayImg = await loadImage(overlaySrc);

      const stream = exportCanvas.captureStream(fps);
      const mimeType = pickSupportedVideoMimeType();

      let recorder;
      try {
        recorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);
      } catch (e) {
        console.warn(
          "[PreviewPane] MediaRecorder init failed, retrying default",
          e
        );
        recorder = new MediaRecorder(stream);
      }

      console.log(
        "[PreviewPane] video export mimeType:",
        recorder.mimeType || mimeType || "(default)"
      );

      const chunks = [];

      recorder.ondataavailable = (e) => {
        console.log("[PreviewPane] video chunk:", e.data?.size || 0);
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };

      recorder.onerror = (e) => {
        console.warn("[PreviewPane] recorder error", e);
      };

      recorder.onstart = () => {
        console.log("[PreviewPane] recorder started");
      };

      const startPos = camera.position.clone();
      const startTarget = controls.target.clone();
      const startOffset = new THREE.Vector3().subVectors(startPos, startTarget);
      const startDistance = startOffset.length();

      function easeOutCubic(u) {
        return 1 - Math.pow(1 - u, 3);
      }
      function easeInOutCubic(u) {
        return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
      }
      function easeOutQuart(u) {
        return 1 - Math.pow(1 - u, 4);
      }
      function easeInOutSine(u) {
        return -(Math.cos(Math.PI * u) - 1) / 2;
      }
      const easeByName = {
        easeOutCubic,
        easeInOutCubic,
        easeOutQuart,
        easeInOutSine,
      };
      function lerp(a, b, t) {
        return a + (b - a) * t;
      }

      // Preset tuning — each export is a 3-phase camera arc:
      //   Phase 1: drift in to establish framing.
      //   Phase 2: the showcase move — punch in (Push) or big
      //            horizontal orbit (Sweep).
      //   Phase 3: recoil/settle to the final beauty framing so the
      //            closing frame feels deliberate. The old preset's
      //            phase 3 went from 0.74× → 0.75× distance — basically
      //            frozen — which is what made the previous push feel
      //            like it "just zooms and stops." The new push actually
      //            recoils from a tight 0.52× back to 0.72×.
      let phase1End = 0.30;
      let phase2End = 0.55;
      let phase1DistanceFactor = 0.72;
      let phase2DistanceFactor = 0.52;
      let phase3DistanceFactor = 0.72;
      let phase2OrbitMax = 0;
      let phase3OrbitMax = 0.15;
      let phase1Easing = "easeInOutCubic";
      let phase2Easing = "easeOutQuart";
      let phase3Easing = "easeInOutSine";

      if (preset === "sweepRight" || preset === "sweepLeft") {
        const dir = preset === "sweepLeft" ? -1 : 1;
        // Sweep: shorter drift-in so the actual sweep gets most of the
        // duration. ~57° horizontal arc (vs the old ~13°) so the move
        // actually reads as going around the figure, plus a small pull-
        // back on landing for the same "intentional final frame" effect
        // the push has.
        phase1End = 0.20;
        phase2End = 0.85;
        phase1DistanceFactor = 0.82;
        phase2DistanceFactor = 0.82;
        phase3DistanceFactor = 0.92;
        phase2OrbitMax = 1.0 * dir;
        phase3OrbitMax = 1.0 * dir;
        phase1Easing = "easeInOutCubic";
        phase2Easing = "easeInOutSine";
        phase3Easing = "easeOutCubic";
      }

      const startTime = performance.now();

      await new Promise((resolve) => requestAnimationFrame(resolve));
      recorder.start(250);

      return await new Promise((resolve) => {
        function animate() {
          const now = performance.now();
          const t = Math.min((now - startTime) / durationMs, 1);

          let curDistance = startDistance;
          let orbitAngle = 0;

          if (t <= phase1End) {
            // Phase 1: drift in to establish framing.
            const u = t / phase1End;
            const e = easeByName[phase1Easing](u);
            curDistance = lerp(
              startDistance,
              startDistance * phase1DistanceFactor,
              e
            );
            orbitAngle = 0;
          } else if (t <= phase2End) {
            // Phase 2: showcase move — punch-in (Push) or big sweep.
            const u = (t - phase1End) / (phase2End - phase1End);
            const e = easeByName[phase2Easing](u);
            curDistance = lerp(
              startDistance * phase1DistanceFactor,
              startDistance * phase2DistanceFactor,
              e
            );
            orbitAngle = lerp(0, phase2OrbitMax, e);
          } else {
            // Phase 3: recoil / settle to the final beauty frame.
            const u = (t - phase2End) / (1 - phase2End);
            const e = easeByName[phase3Easing](u);
            curDistance = lerp(
              startDistance * phase2DistanceFactor,
              startDistance * phase3DistanceFactor,
              e
            );
            orbitAngle = lerp(phase2OrbitMax, phase3OrbitMax, e);
          }

          const rel = startOffset
            .clone()
            .normalize()
            .multiplyScalar(curDistance);

          rel.applyAxisAngle(new THREE.Vector3(0, 1, 0), orbitAngle);

          camera.position.copy(startTarget.clone().add(rel));
          controls.target.copy(startTarget);
          controls.update();

          // --- composite current frame into export canvas ---
          ctx.clearRect(0, 0, EXPORT_W, EXPORT_H);

          const scale = Math.max(
            EXPORT_W / srcCanvas.width,
            EXPORT_H / srcCanvas.height
          );

          const w = srcCanvas.width * scale;
          const h = srcCanvas.height * scale;
          const x = (EXPORT_W - w) / 2;
          const y = (EXPORT_H - h) / 2;

          ctx.drawImage(srcCanvas, x, y, w, h);

          // subtle vignette
          const grad = ctx.createRadialGradient(
            EXPORT_W / 2,
            EXPORT_H / 2,
            EXPORT_W * 0.25,
            EXPORT_W / 2,
            EXPORT_H / 2,
            EXPORT_W * 0.8
          );
          grad.addColorStop(0, "rgba(0,0,0,0)");
          grad.addColorStop(1, "rgba(0,0,0,0.28)");
          ctx.fillStyle = grad;
          ctx.fillRect(0, 0, EXPORT_W, EXPORT_H);

          // overlay frame
          ctx.drawImage(overlayImg, 0, 0, EXPORT_W, EXPORT_H);

          // text
          const activeLayer =
            (tattoo.layers || []).find((l) => l.id === tattoo.editingLayerId) ||
            tattoo.layers?.[0] ||
            null;

          const designName = activeLayer?.name || "Tattoo Preview";
          const artistName = activeLayer?.ownerShopName || "Custom Design";

          const padding = 60;
          const footerY = EXPORT_H - 220;

          ctx.textBaseline = "top";
          ctx.shadowColor = "rgba(0,0,0,0.55)";
          ctx.shadowBlur = 12;
          ctx.shadowOffsetY = 3;

          ctx.fillStyle = "#fff";
          ctx.font = "56px Staatliches, Impact, sans-serif";
          ctx.fillText(String(designName).toUpperCase(), padding, footerY + 40);

          ctx.fillStyle = "rgba(255,255,255,0.88)";
          ctx.font = "30px Poppins, sans-serif";
          ctx.fillText(
            activeLayer?.ownerShopName
              ? `Flash by ${artistName}`
              : "Custom Design",
            padding,
            footerY + 110
          );

          ctx.shadowBlur = 0;
          ctx.shadowOffsetY = 0;

          ctx.fillStyle = "rgba(255,255,255,0.55)";
          ctx.font = "26px Poppins, sans-serif";
          ctx.fillText("tattune.ca", EXPORT_W - 170, footerY + 112);

          if (t < 1) {
            requestAnimationFrame(animate);
          } else {
            requestAnimationFrame(() => {
              try {
                recorder.requestData?.();
              } catch {}

              recorder.stop();

              camera.position.copy(startPos);
              controls.target.copy(startTarget);
              controls.update();
            });
          }
        }

        recorder.onstop = () => {
          const finalType = recorder.mimeType || mimeType || "video/webm";
          const blob = new Blob(chunks, { type: finalType });

          console.log("[PreviewPane] recorder stopped", {
            mimeType: finalType,
            chunks: chunks.length,
            blobSize: blob.size,
            preset,
          });

          if (!blob.size) {
            resolve(null);
            return;
          }

          const url = URL.createObjectURL(blob);
          resolve({ url, blob, mimeType: finalType });
        };

        requestAnimationFrame(animate);
      });
    },

    async captureExportPng(opts = {}) {
      const gl = glRef.current;
      const camera = cameraRef.current;
      const scene = sceneRef.current;

      if (!gl || !gl.domElement || !camera || !scene) {
        console.warn("[PreviewPane] captureExportPng: missing gl/camera/scene");
        return null;
      }

      const {
        width = 2048,
        height = 2048,
        format = "image/png",
        quality = 0.98,
        // Super-sample factor. Render the scene at width*ssaa × height*ssaa,
        // then downsample to the requested width × height via a 2D canvas
        // with high-quality smoothing. This is the workaround for the
        // renderer being created with `antialias: !isIOS` — on iOS there
        // is no MSAA at all, so the captured frame would otherwise be
        // visibly jagged. 2x SSAA gives a clean result; 1.5x on iOS keeps
        // memory pressure manageable (a 4096² framebuffer is ~64 MB).
        ssaa = isIOS ? 1.5 : 2,
      } = opts || {};

      try {
        const prevSize = gl.getSize(new THREE.Vector2());
        const prevPixelRatio = gl.getPixelRatio();

        const renderW = Math.round(width * ssaa);
        const renderH = Math.round(height * ssaa);

        // Push the supersampled export resolution onto the renderer.
        gl.setPixelRatio(1);
        gl.setSize(renderW, renderH, false);

        if (camera.isPerspectiveCamera || camera.isOrthographicCamera) {
          camera.aspect = renderW / renderH;
          camera.updateProjectionMatrix();
        }

        gl.render(scene, camera);

        // Grab the supersampled frame. toDataURL is OK with
        // preserveDrawingBuffer: false because we're calling it
        // synchronously after the render with no repaint in between.
        const hiResUrl = gl.domElement.toDataURL("image/png");

        // Restore the live renderer immediately so we don't leave it in
        // a weird size if anything below throws.
        gl.setPixelRatio(prevPixelRatio);
        gl.setSize(prevSize.x, prevSize.y, false);

        if (camera.isPerspectiveCamera || camera.isOrthographicCamera) {
          camera.aspect = prevSize.x / prevSize.y;
          camera.updateProjectionMatrix();
        }

        gl.render(scene, camera);

        // Downsample to the requested output size via a 2D canvas with
        // high-quality bilinear filtering. Effectively SSAA: each output
        // pixel is the average of (ssaa²) source pixels, killing the
        // aliasing that the on-iOS MSAA-off path would otherwise leave
        // behind in the export.
        const hiResImg = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = hiResUrl;
        });

        const outCanvas = document.createElement("canvas");
        outCanvas.width = width;
        outCanvas.height = height;
        const ctx = outCanvas.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(hiResImg, 0, 0, width, height);

        const dataUrl =
          format === "image/jpeg"
            ? outCanvas.toDataURL("image/jpeg", quality)
            : outCanvas.toDataURL("image/png");

        console.log("[PreviewPane] captureExportPng", {
          out: `${width}x${height}`,
          internal: `${renderW}x${renderH}`,
          ssaa,
          format,
          length: dataUrl.length,
        });

        return dataUrl;
      } catch (e) {
        console.warn("[PreviewPane] captureExportPng failed", e);
        return null;
      }
    },

    // ✅ NEW: capture the current view (camera + orbit target)
    getViewSnap() {
      const cam = cameraRef.current;
      const ctl = controlsRef.current;
      if (!cam || !ctl) return null;

      return {
        pos: [cam.position.x, cam.position.y, cam.position.z],
        target: [ctl.target.x, ctl.target.y, ctl.target.z],
        fov: cam.fov,
        zoom: cam.zoom,
      };
    },

    // ✅ NEW: apply a view snapshot
    applyViewSnap(v) {
      const cam = cameraRef.current;
      const ctl = controlsRef.current;
      if (!cam || !ctl || !v) return false;

      const [cx, cy, cz] = v.pos || [];
      const [tx, ty, tz] = v.target || [];

      if ([cx, cy, cz].every(Number.isFinite)) cam.position.set(cx, cy, cz);
      if ([tx, ty, tz].every(Number.isFinite)) ctl.target.set(tx, ty, tz);

      if (Number.isFinite(v.fov)) cam.fov = v.fov;
      if (Number.isFinite(v.zoom)) cam.zoom = v.zoom;

      cam.updateProjectionMatrix();
      ctl.update?.();
      return true;
    },
  }));

  useEffect(() => {
    window.tt_getCam = () =>
      getCameraSnapshot(cameraRef.current, controlsRef.current);
    return () => {
      try {
        delete window.tt_getCam;
      } catch {}
    };
  }, []);

  useEffect(() => {
    const onSetCam = (e) => {
      const snap = e.detail?.cam;
      const cam = cameraRef.current;
      const ctr = controlsRef.current;
      if (!snap || !cam) return;

      // ✅ APPLY the camera immediately (next frame so controls are ready)
      requestAnimationFrame(() => {
        applyCameraSnapshot(cam, ctr, snap);
      });

      // ✅ then set your shared/lock flags
      window.__tt_shared_cam_applied = true;
      window.__tt_skip_fit_once = true;
      window.__tt_cam_lock_until = performance.now() + 2000;
      window.__tt_lock_cam = { snap, until: window.__tt_cam_lock_until };

      sharedCamRef.current = true;
      lastSharedCamRef.current = snap || null;
    };

    window.addEventListener("tt:set-camera", onSetCam);
    return () => window.removeEventListener("tt:set-camera", onSetCam);
  }, []);

  function decodeCamParam(s) {
    try {
      const json = atob(decodeURIComponent(s));
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  function getSurfaceCursor() {
    if (decalDragRef.current.dragging) return "grabbing";

    if (isDecalModeActive()) {
      return isPointerNearActiveDecal?.() ? "grab" : "default";
    }

    return "grab"; // orbit
  }

  function readUrlFlags() {
    try {
      const hash =
        (typeof window !== "undefined" && window.location.hash) || "";
      const qs = new URLSearchParams(
        hash.startsWith("#") ? hash.slice(1) : hash
      );
      const r = qs.get("r");
      const focus = qs.get("focus");
      const camRaw = qs.get("cam");
      const cam = camRaw ? decodeCamParam(camRaw) : null;
      const out = {};
      if (r != null) out.r = r === "1" || r === "true";
      if (focus) out.focus = focus;
      if (cam) out.cam = cam;
      return Object.keys(out).length ? out : null;
    } catch {
      return null;
    }
  }

  const consumeUrlShare = useCallback(() => {
    const key = getShareKey?.() ?? null;
    if (key && consumedShareKeyRef.current === key) return false;

    const cam = cameraRef.current;
    const ctrls = controlsRef.current;
    if (!cam) return false;

    const p = readUrlFlags?.() || window.__tt_pending_share || null;
    if (!p) return false;

    // ✅ mark consumed no matter what flags are present
    consumedShareKeyRef.current = key || "__no_key__";

    if (p.cam) {
      console.log("[CAMTRACE] consumeUrlShare applying p.cam", p.cam);
      tattoo.viewState = p.cam; // ✅ make it the source of truth immediately
      window.__tt_pending_share = {
        ...(window.__tt_pending_share || {}),
        cam: p.cam,
      };
      applyCameraSnapshot(cam, ctrls, p.cam);

      try {
        if (window.__tt_pending_share?.cam) {
          window.__tt_pending_share.cam = null;
        }
      } catch {}

      window.__tt_shared_cam_applied = true;
      window.__tt_cam_lock_until = performance.now() + 2500;
      window.__tt_lock_cam = { snap: p.cam, until: window.__tt_cam_lock_until };

      sharedCamRef.current = true;
      lastSharedCamRef.current = p.cam || null;
    }

    if (p.focus) {
      try {
        tattoo.editingLayerId = p.focus;
      } catch {}
      setTimeout(() => {
        try {
          window.dispatchEvent(
            new CustomEvent("tt:open-layer", { detail: { layerId: p.focus } })
          );
          window.dispatchEvent(
            new CustomEvent("tt:focus-comments", {
              detail: { layerId: p.focus },
            })
          );
        } catch {}
      }, 120);
    }

    return true;
  }, [readUrlFlags, getShareKey]);

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        consumeUrlShare();
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [consumeUrlShare]);

  useEffect(() => {
    const fn = () => {
      try {
        consumeUrlShare();
      } catch (e) {
        console.warn("[share] late consume error", e);
      }
    };
    window.addEventListener("tt:scene-ready", fn);
    return () => window.removeEventListener("tt:scene-ready", fn);
  }, [consumeUrlShare]);

  useEffect(() => {
    if (!guidePaintRef.current) {
      guidePaintRef.current = new GuidePaint({ size: 2048 });
      console.log("[gp] created");
    }
    try {
      // eslint-disable-next-line no-undef
      installGPColorBridge?.();
    } catch {}
    return () => {
      try {
        guidePaintRef.current?.detach?.();
      } catch {}
    };
  }, []);

  useEffect(() => {
    const gp = guidePaintRef.current;
    const url = snap.guidePaintPng;

    if (!gp || !url) return;

    const canvas = gp.getCanvas?.();
    const ctx = canvas?.getContext?.("2d");
    if (!canvas || !ctx) return;

    let cancelled = false;
    const img = new Image();

    img.onload = () => {
      if (cancelled) return;
      try {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        if (gp.texture) gp.texture.needsUpdate = true;
        gp._dirty = true;
        try {
          window.dispatchEvent(new Event("guidepaint:stroke"));
        } catch {}
      } catch (e) {
        console.warn("[PreviewPane] failed to restore guide paint", e);
      }
    };

    img.src = url;

    return () => {
      cancelled = true;
    };
  }, [snap.guidePaintPng]);

  useEffect(() => {
    const roots = (window.__tattoo_roots || []).filter(Boolean);
    roots.forEach((r) => applyMorphsToRoot(r, snap.morphs || {}));
    try {
      window.dispatchEvent(new CustomEvent("morph:changed"));
    } catch {}
  }, [snap.morphs]);

  useEffect(() => {
    const t = setTimeout(() => {
      const obj =
        fullRef.current && (fullRef.current.getObject?.() || fullRef.current);
      if (obj) {
        registerTattooRoot(obj, 0);
        applyMorphsToRoot(obj, snap.morphs || {});
      }
    }, 0);
    return () => clearTimeout(t);
  }, [modelKey, snap.morphs]);

  useEffect(() => {
    const onToggle = (e) => {
      const on = !!e.detail?.on;
      if (controlsRef.current) controlsRef.current.enabled = !on;
      document.body.classList.toggle("painting-on", on);
      console.log("[gp] enabled:", on);
    };
    window.addEventListener("guidepaint:toggle", onToggle);
    return () => window.removeEventListener("guidepaint:toggle", onToggle);
  }, []);

  useEffect(() => {
    const ctrls = controlsRef.current;
    const cam = cameraRef.current;
    if (!ctrls || !cam) return;

    const onEnd = () => {
      const snap = {
        pos: cam.position.toArray(),
        target: ctrls.target.toArray(),
        fov: cam.fov,
        zoom: cam.zoom,
        at: Date.now(),
      };

      tattoo.viewState = snap;

      // ✅ make autosave see this as a real change
      markDirty("camera-end");

      // ✅ and force a tick right now
      try {
        window.dispatchEvent(new Event("tt:autosave-flush"));
      } catch {}
    };

    ctrls.addEventListener("end", onEnd);
    return () => ctrls.removeEventListener("end", onEnd);
  }, []);

  useEffect(() => {
    // orbit only in VIEW mode
    setOrbitEnabled(!isEditMode);

    if (isEditMode) {
      // entering edit: clear any touch state now
      resetOrbitTouchState();
      return;
    }

    // leaving edit → view: OrbitControls will mount now.
    // Wait 1-2 frames so controlsRef.current exists, then reset.
    if (needOrbitResetRef.current) {
      needOrbitResetRef.current = false;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resetOrbitTouchState();
          setOrbitEnabled(true);
        });
      });
    }
  }, [isEditMode]);

  const fitRefs = useMemo(() => [fullRef], []);

  const femaleFullUrl =
    snap.modelUrls?.female?.full || "/tattoo/models/Female.glb";
  const maleFullUrl = snap.modelUrls?.male?.full || "/tattoo/models/Male.glb";

  const wireGuidePaint = useCallback(
    (root) => {
      const gp = guidePaintRef.current;
      const canvasEl = canvasElRef.current;
      const cam = cameraRef.current;
      const ctrls = controlsRef.current;

      if (!gp || !canvasEl || !cam || !root) {
        console.warn("[gp] wire skipped – missing", {
          gp: !!gp,
          canvas: !!canvasEl,
          cam: !!cam,
          root: !!root,
        });
        return;
      }

      if (ctrls) gp.setOrbitControls(ctrls);

      const proxyName =
        sex === "female" ? "FemalePaintProxy" : "MalePaintProxy";
      const hrName = sex === "female" ? "FemaleHR" : "MaleHR";
      const baseName = sex === "female" ? "Female" : "Male";

      const proxyObj = root.getObjectByName(proxyName);
      if (proxyObj) {
        proxyObj.userData.ttRole = "paintProxy";
        proxyObj.traverse((o) => (o.userData.ttRole = "paintProxy"));
      }

      // Put proxy on its own layer so it NEVER renders
      if (proxyObj) {
        proxyObj.traverse((o) => {
          if (!o.isMesh && !o.isSkinnedMesh) return;

          // IMPORTANT: keep visible so Raycaster can hit it
          o.visible = true;
          o.frustumCulled = false;

          // make it render-invisible (but raycastable)
          const m = new THREE.MeshBasicMaterial({
            transparent: true,
            opacity: 0,
            depthWrite: false,
            depthTest: true,
          });
          m.colorWrite = false; // key: nothing is written to color buffer
          o.material = m;
          o.material.needsUpdate = true;
        });
      }

      // Find an actual mesh under proxy (group -> first mesh)
      let proxyMesh = null;
      if (proxyObj) {
        if (proxyObj.isMesh || proxyObj.isSkinnedMesh) {
          proxyMesh = proxyObj;
        } else {
          proxyObj.traverse((o) => {
            if (proxyMesh) return;
            if (o?.isMesh || o?.isSkinnedMesh) proxyMesh = o;
          });
        }
      }

      const body =
        root.getObjectByName(hrName) ||
        root.getObjectByName(baseName) ||
        findBodySkinnedMesh(root, sex) ||
        pickBestSkinnedMesh(root);

      const target = proxyMesh || body;
      window.__tt_decal_pick_mesh = proxyMesh || null;

      if (!target) {
        console.warn("[gp] wire failed – no proxy/body target found");
        return;
      }

      gp.setTargets([target], cam, canvasEl);

      console.log("[gp] wired – target:", target.name, "proxy?", !!proxyMesh);
    },
    [sex]
  );

  const isEditingDecals =
    tattoo.decalMode === "drag" || tattoo.decalMode === "place";

  const toggleDecalMode = React.useCallback(() => {
    const wasEditing =
      tattoo.decalMode === "drag" || tattoo.decalMode === "place";

    const gp = guidePaintRef.current;
    const next = wasEditing ? "view" : "drag";

    // entering edit: disable paint + remove mapping constraints for decal picking
    if (next !== "view" && gp) {
      if (!prevGpMapRef.current) {
        prevGpMapRef.current = {
          uvRect: gp.uvRect ? { ...gp.uvRect } : { x: 0, y: 0, w: 1, h: 1 },
          calib: gp.calib
            ? { ...gp.calib }
            : { uScale: 1, vScale: 1, uBias: 0, vBias: 0 },
        };
      }
      try {
        gp.setEnabled(false);
      } catch {}
      try {
        gp._lastUV = gp._lastHitPoint = gp._lastRayHit = null;
      } catch {}
      try {
        gp.setUvRect({ x: 0, y: 0, w: 1, h: 1 });
      } catch {}
      try {
        gp.setCalibration({ uScale: 1, vScale: 1, uBias: 0, vBias: 0 });
      } catch {}
    }

    // clear latched drag state when switching modes
    if (decalDragRef.current?.dragging) {
      ttResetDecalDragState({ forceHigh: false, reason: "cancel-drag" });
    } else {
      ttResetDecalDragState({ forceHigh: false });
    }

    tattoo.decalMode = next;

    // entering edit: clear again (only if not dragging)
    if (next !== "view") {
      if (!decalDragRef.current?.dragging) {
        ttResetDecalDragState({ forceHigh: false });
      }
      return;
    }

    // leaving edit: restore GuidePaint mapping constraints
    if (gp && prevGpMapRef.current) {
      try {
        gp.setUvRect(prevGpMapRef.current.uvRect);
      } catch {}
      try {
        gp.setCalibration(prevGpMapRef.current.calib);
      } catch {}
      prevGpMapRef.current = null;
      try {
        gp._lastUV = gp._lastHitPoint = gp._lastRayHit = null;
      } catch {}
    }

    // leaving edit → view
    try {
      window.__tt_cam_gesture = false;
      window.__tt_lock_cam = null;
      window.__tt_cam_lock_until = 0;
    } catch {}

    // snap crisp once
    if (!decalDragRef.current?.dragging) {
      ttResetDecalDragState({ forceHigh: true });
    }

    if (isIOS && didEditGestureRef.current) {
      pendingOrbitSnapRef.current = getCameraSnapshot(
        cameraRef.current,
        controlsRef.current
      );
      didEditGestureRef.current = false;
      setOrbitKey((k) => k + 1);
    } else {
      didEditGestureRef.current = false;
      resetOrbitTouchState();
      setOrbitEnabled(true);
    }
  }, [isIOS, setOrbitKey, setOrbitEnabled]);

  return (
    <div
      className="preview-root tt-col tt-grow"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{ position: "relative" }}
    >
      {(tattoo.layers?.length || 0) === 0 && !isDragOver && (
        <div className="tt-drop-hint">
          Drop a tattoo on the Plannequin
          <div className="tt-drop-hint-sub">or browse the flash gallery</div>
        </div>
      )}

      {isDragOver && (
        <div className="tt-drop-overlay">Drop tattoo on the Plannequin</div>
      )}
      <div className="tt-row tt-toolbar" style={{ marginBottom: 10 }}>
        <ProjectHeart />

        {!readOnly && (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="tt-btn"
              onClick={() => {
                try {
                  saveProjectToFile();
                } catch (e) {
                  console.warn("[Save] failed", e);
                }
              }}
            >
              <DockIcon icon={Save} />
            </button>

            <button
              type="button"
              className="tt-btn"
              onClick={() => {
                try {
                  window.dispatchEvent(new Event("tattoo:open-dashboard"));
                } catch {}
              }}
              style={{ minWidth: 40 }}
            >
              <DockIcon icon={LayoutDashboard} />
            </button>
          </div>
        )}

        <button
          type="button"
          className="tt-btn"
          onClick={() => {
            const cam = cameraRef.current;
            const ctrls = controlsRef.current;
            if (!cam || !ctrls) return;

            const saved = safeLoadHome(modelKey);
            const snap = saved || getDefaultCamSnap(sex);

            // ✅ keepTarget: true = do NOT wreck the pivot you just centered
            easeTo(cam, ctrls, snap, 420, { keepTarget: true });

            try {
              window.dispatchEvent(new Event("tt:idle-reset"));
            } catch {}
          }}
          title="Reset view"
        >
          <DockIcon icon={Monitor} />
        </button>
        {/* ✅ Reset buttons */}

        {/*
        <button
          type="button"
          className="tt-btn"
          onClick={() => {
            const cam = cameraRef.current;
            const ctrls = controlsRef.current;
            if (!cam || !ctrls) return;

            const snap = getCameraSnapshot(cam, ctrls);
            saveHomeSnap(modelKey, snap);
          }}
          title="Save current view as the Home / Reset view"
        >
          <DockIcon icon={Camera} />
        </button> */}
        <button
          type="button"
          className={`tt-btn move-icon ${isEditingDecals ? "is-active" : ""}`}
          onClick={toggleDecalMode}
          title={
            isEditingDecals
              ? "Exit decal edit (orbit/zoom)"
              : "Edit decals (drag)"
          }
        >
          <Move size={18} strokeWidth={2.25} />
        </button>
        <button
          type="button"
          className={`tt-btn move-icon ${
            mode === "morphs" && open ? "is-active" : ""
          }`}
          title={
            mode === "morphs" && open ? "Hide body morphs" : "Show body morphs"
          }
          onClick={() => {
            // NEW: morphs live in the design panel bottom sheet
            if (onOpenMorphs) {
              onOpenSheet?.(); // open the sheet (optional but feels right)
              onOpenMorphs(); // set panelMode="morphs"
              return;
            }

            // fallback (if not wired): keep old behavior
            if (mode === "morphs" && open) {
              setOpen(false);
              setMode("paint");
            } else {
              setMode("morphs");
              setOpen(true);
            }
          }}
        >
          <Dumbbell size={18} strokeWidth={2.25} />
        </button>

        {showBackToStudio && (
          <button
            type="button"
            onClick={onBackToStudio}
            className="tt-btn tt-btn--icon"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              minWidth: 120,
            }}
            title="Back to your studio"
          >
            <span>←</span>
            <span style={{ fontSize: 13 }}>Your Studio</span>
          </button>
        )}

        <div className="tt-right tt-row" style={{ gap: 8 }}>
          {isMobile && (
            <button
              type="button"
              className={`tt-btn move-icon ${leftOpen ? "is-active" : ""}`}
              onClick={onToggleDesignPanel}
              title={leftOpen ? "Hide design panel" : "Show design panel"}
            >
              <SlidersHorizontal size={18} strokeWidth={2.25} />
            </button>
          )}
          <SaveShareButton className="tt-btn tt-btn--fill-primary" />
        </div>
      </div>

      <div
        className="preview-canvas-wrap tt-panel"
        style={{ position: "relative" }}
      >
        {!isMobile && (
          <OverlayControls
            refs={[fullRef]}
            showMorph={true}
            controlsRef={controlsRef}
          />
        )}
        {isMobile && (
          <MobileBottomDock
            onOpenMorphs={onOpenMorphs}
            onOpenSheet={onOpenSheet}
          />
        )}
        {isTouch && (
          <ThumbPaintTogglePad side="left" size={68} label="Hold to paint" />
        )}

        <div
          ref={surfaceRef}
          className="canvas-gesture-surface"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 5,
            pointerEvents: isEditMode ? "auto" : "none", // ✅ only intercept in edit
            touchAction: "none",
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
          onPointerDownCapture={
            isEditMode ? onSurfacePointerDownCapture : undefined
          }
          onPointerMoveCapture={
            isEditMode ? onSurfacePointerMoveCapture : undefined
          }
          onPointerUpCapture={
            isEditMode ? onSurfacePointerUpCapture : undefined
          }
          onPointerCancelCapture={
            isEditMode ? onSurfacePointerCancelCapture : undefined
          }
        >
          <div
            style={{ position: "relative", width: "100%", height: "100%" }}
          ></div>
          <Canvas
            frameloop="always"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              display: "block",
            }}
            camera={{
              position: [-0.7, 1.0, 2.2],
              fov: 35,
              near: 0.01,
              far: 100,
            }}
            dpr={[1, 2]}
            onContextMenu={(e) => e.preventDefault()}
            onCreated={(state) => {
              canvasElRef.current = state.gl.domElement;
              cameraRef.current = state.camera;
              state.camera.layers.enable(0);
              glRef.current = state.gl;
              setGlReady((n) => n + 1);

              // ✅ add this block
              try {
                const gl = state.gl;
                const ctx = gl.getContext?.();
                const ext = ctx?.getExtension?.("WEBGL_lose_context");
                if (!ext) {
                  gl.forceContextLoss = () => {};
                  gl.forceContextRestore = () => {};
                }
              } catch {}

              try {
                canvasElRef.current.style.touchAction = "none";
              } catch {}

              consumeUrlShare();
              setTimeout(consumeUrlShare, 300);
              setTimeout(consumeUrlShare, 1000);
            }}
            gl={{
              outputColorSpace: THREE.SRGBColorSpace,
              toneMapping: THREE.ACESFilmicToneMapping,
              toneMappingExposure: 0.9,
              antialias: !isIOS,
              preserveDrawingBuffer: false,
              powerPreference: "high-performance",
              stencil: false,
              depth: true,
              alpha: true,
              physicallyCorrectLights: true,
            }}
            shadows={false}
          >
            <SceneBinder
              sceneRef={sceneRef}
              glRef={glRef}
              cameraRef={cameraRef}
            />
            <WebGLCleanup />
            <InvalidateOnEvents controlsRef={controlsRef} />

            <fog attach="fog" args={["#121212", 0, 15]} />
            <color attach="background" args={["#131313"]} />

            <CameraFollowLightRig rimIntensity={6} rimIntensity2={5} />
            <Environment
              files="/hdr/potsdamer_platz_1k.hdr"
              background={false}
              intensity={0.15}
            />

            <FreezeAspect />

            {/*{showLogo && (
              <LogoOverlay
                src="/tattoo/images/TatTune.png"
                sizeV={0.14}
                marginV={0.035}
                marginH={0.045}
                opacity={0.8}
              />
            )}
            
            <IdleMotion
              modelRef={fullRef}
              enabled={!snap.leftOpen}
              intensity={0.8}
            />*/}

            <Suspense fallback={null}>
              {sex === "female" && (
                <PartModel
                  key={modelKey}
                  ref={fullRef}
                  modelUrl={femaleFullUrl}
                  nodeNameRegex={/.*/i}
                  skinColor={snap.skinColor}
                  skinRoughness={snap.skinRoughness ?? 0.65}
                  skinMetalness={snap.skinMetalness ?? 0.0}
                  skinEnvIntensity={snap.skinEnvIntensity ?? 0.25}
                  skinNormalScale={snap.skinNormalScale ?? 1.0}
                  overlayOpacity={snap.overlayOpacity}
                  edgeClip={snap.edgeClip}
                  layers={filteredLayers}
                  partKey={partKey}
                  targetHeight={1.65}
                  yOffset={isMobile ? 0 : 0}
                  uvImage={snap.uvImage}
                  tattooKey={tattooKey}
                  // ✅ NEW: pose morph system
                  poseWeights={snap.poseWeights}
                  onPoseMorphNames={(names) => setPoseMorphNames(names)}
                  onReady={(root) => {
                    console.log("[preview] PartModel ready", sex);

                    registerTattooRoot(root, 0);

                    // morphs are fine here
                    try {
                      applyMorphsToRoot(root, tattoo.morphs || {});
                    } catch {}

                    // ✅ 1) snap FIRST so bounds are final before any camera/view logic runs
                    snapRootToFloor(root, 0);
                    root.updateWorldMatrix?.(true, true);

                    // ✅ set a stable full-body default view for fresh sessions
                    if (!tattoo.viewState && !window.__tt_shared_cam_applied) {
                      const cam = cameraRef.current;
                      const ctrls = controlsRef.current;
                      if (cam && ctrls) {
                        const def = getDefaultCamSnap(sex); // we’ll make sure this is full-body
                        applyCameraSnapshot(cam, ctrls, def);
                        tattoo.viewState = def;

                        // lock briefly so nothing else reframes
                        window.__tt_cam_lock_until = performance.now() + 900;
                        window.__tt_lock_cam = {
                          snap: def,
                          until: window.__tt_cam_lock_until,
                        };
                      }
                    }

                    // ✅ 2) wire GP after snap (ray targets stable)
                    wireGuidePaint(root);

                    // ✅ 4) finally tell the app "scene ready" (camera snapshot apply happens now, correctly)
                    window.dispatchEvent(new CustomEvent("tt:scene-ready"));

                    try {
                      typeof onReady === "function" && onReady();
                    } catch {}
                  }}
                />
              )}

              {sex === "male" && (
                <PartModel
                  key={modelKey}
                  ref={fullRef}
                  modelUrl={maleFullUrl}
                  nodeNameRegex={/.*/i}
                  skinColor={snap.skinColor}
                  skinRoughness={snap.skinRoughness ?? 0.65}
                  skinMetalness={snap.skinMetalness ?? 0.0}
                  skinEnvIntensity={snap.skinEnvIntensity ?? 0.35}
                  skinNormalScale={snap.skinNormalScale ?? 1.0}
                  overlayOpacity={snap.overlayOpacity}
                  edgeClip={snap.edgeClip}
                  layers={filteredLayers}
                  partKey={partKey}
                  targetHeight={1.75}
                  yOffset={isMobile ? 0 : 0}
                  uvImage={snap.uvImage}
                  tattooKey={tattooKey}
                  // ✅ NEW: pose morph system
                  poseWeights={snap.poseWeights}
                  onPoseMorphNames={(names) => setPoseMorphNames(names)}
                  onReady={(root) => {
                    console.log("[preview] PartModel ready", sex);
                    console.count("[PartModel] render");

                    registerTattooRoot(root, 0);

                    // morphs are fine here
                    try {
                      applyMorphsToRoot(root, tattoo.morphs || {});
                    } catch {}

                    // ✅ 1) snap FIRST so bounds are final before any camera/view logic runs
                    snapRootToFloor(root, 0);
                    root.updateWorldMatrix?.(true, true);
                    // ✅ set a stable full-body default view for fresh sessions
                    if (!tattoo.viewState && !window.__tt_shared_cam_applied) {
                      const cam = cameraRef.current;
                      const ctrls = controlsRef.current;
                      if (cam && ctrls) {
                        const def = getDefaultCamSnap(sex); // we’ll make sure this is full-body
                        applyCameraSnapshot(cam, ctrls, def);
                        tattoo.viewState = def;

                        // lock briefly so nothing else reframes
                        window.__tt_cam_lock_until = performance.now() + 900;
                        window.__tt_lock_cam = {
                          snap: def,
                          until: window.__tt_cam_lock_until,
                        };
                      }
                    }

                    // ✅ 2) wire GP after snap (ray targets stable)
                    wireGuidePaint(root);

                    // ✅ 4) finally tell the app "scene ready" (camera snapshot apply happens now, correctly)
                    window.dispatchEvent(new CustomEvent("tt:scene-ready"));

                    try {
                      typeof onReady === "function" && onReady();
                    } catch {}
                  }}
                />
              )}

              <CameraLock cameraRef={cameraRef} controlsRef={controlsRef} />
            </Suspense>

            {!isIOS && (
              <mesh
                receiveShadow
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, 0.33, 0]}
              >
                <planeGeometry args={[50, 50]} />
                <MeshReflectorMaterial
                  blur={[20, 20]}
                  resolution={1024}
                  mixBlur={1}
                  mixStrength={10}
                  roughness={0.7}
                  depthScale={1}
                  minDepthThreshold={0.4}
                  maxDepthThreshold={1.4}
                  color="#101010"
                  metalness={0.9}
                />
              </mesh>
            )}

            <ForceFrame
              forceFrameRef={forceFrameRef}
              cameraRef={cameraRef}
              controlsRef={controlsRef}
            />

            <OrbitControls
              key={orbitKey}
              ref={controlsRef}
              enablePan
              enableZoom
              enableRotate
              enableDamping
              dampingFactor={0.15}
              minDistance={0.02}
              maxDistance={999}
              maxPolarAngle={Math.PI * 1}
            />
          </Canvas>
          {studioLoading && <StudioLoadingOverlay />}
        </div>
      </div>
    </div>
  );
});

export default PreviewPane;
